import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { API, authFetch } from '../utils/api';
import ClientScopeCombo from './ClientScopeCombo';
import ClientScopePanel from './ClientScopePanel';
import LedgerDrawer from './LedgerDrawer';
import CancelAgencyStatements from './CancelAgencyStatements';
import CancelAgencyJournal from './CancelAgencyJournal';
import MalsoCertViewer from './MalsoCertViewer';
import ReceivablesBoard from './ReceivablesBoard';
// 장부 머리말 — 그 달만 세금계산서 발행 여부를 바꾸는 스위치 (W2)
import TaxMonthSwitch from './TaxMonthSwitch';
// 수기 미수 조정 — 진입 버튼(u: 스코프만) · 등록 모달 · 장부 표 아래 조정 목록. '이번 달 청구'에 조정 합을 더한다.
import SettlementAdjustModal, { AdjustButton, SettlementAdjustList, fetchAdjustments, adjustmentTotal } from './SettlementAdjustModal';
// 정산 항목 입력 드로어 (수기 추가 / 항목 수정 공용) — 라벨·인풋 시각 문법(ls/is)의 단일 원천도 여기.
import SettlementItemDrawer, { ls, is, JOB_KINDS, JobKindBadge, jobKindOf } from './SettlementItemDrawer';
// 부가서비스 — 요청과 연결된 정산 항목은 요청 부가서비스 편집기를, 수기(미연결) 항목은 카탈로그 칩 다중 선택을 쓴다 (W8).
import RequestServicesEditor from './RequestServicesEditor';
import ExtraServiceChipTable from './ExtraServiceChipTable';
// 화면 세그먼트 = 밑줄 탭. 대행요청·거래처 헤더 탭과 같은 컴포넌트 (Jin 8/26 통일)
import CaHeaderTabs from './CaHeaderTabs';
// 요약 스트립 칸별 ⓘ 설명 팝오버 (Jin 10/20)
import SettlementStripHelp from './SettlementStripHelp';
import { ensureKakao, shareStatementLink, periodTitle } from '../utils/kakaoShare';
import { useIsMobile, hScroll } from '../utils/useMobile';
import PresenceDot from './PresenceDot';
import PresenceBanner from './PresenceBanner';
import { getSocket } from '../utils/socket';
// 정산 항목 동시 작업 프레즌스 + 실시간 갱신(settlement:updated) — CancelAgency.js cancel:presence 와 대칭.
import { enter as presenceEnter, leave as presenceLeave, createChannel as createPresenceChannel } from '../utils/settlementPresence';
// 표시 헬퍼는 프레즌스 3종 공용 — 정본은 utils/carPresence.js.
import { mySid as presenceMySid, presenceOthersLabeled } from '../utils/carPresence';

// 말소대행 정산 탭 — 엑셀 장부 양식 정합 (W6 웹 admin 패리티).
// 앱 원본: erp/app/lib/features/cancel_agency/screens/settlement_screen.dart (W2)
// 백엔드: routes/cancelAgency.js (/settlement-items, /settlement-payments)
//
// 장부 컬럼: No·월일·[거래처]·차종·차량번호·차대번호·등록세·증지·수수료·[세액(발행형)]·
//            기타·기타(과세)·부가서비스·재교부증지·재교부수수료·재교부세액·소계·비고·구청.
//   (2026-09-01 엑셀 정합 — 말소팀 실물 장부와 같이 기타 계열이 재교부 그룹 **앞**.
//    기타(과세)·부가서비스는 엑셀에 없는 우리 칸이라 '기타' 옆에 둔다.)
//   재교부 그룹 3분할(M1, 2026-08-31) 이후 열 구성 = 정산서 PDF(utils/statementPdf.js detailTable)
//   · 일계표(CancelAgencyJournal COLS) 와 **같은 순서**. 세 화면이 어긋나면 같은 달을 봐도 숫자가 갈린다.
//   '원부' 열은 사라졌다 — 구 원부/구 재교부는 소급 분해하지 않고 재교부수수료 칸에 괄호로 병기한다.
// 일자 그룹 끝마다 소계 행(장부 '합계' 컬럼), 요약에 전월미수→청구→입금→미수합계 흐름.
// 입금은 건별 이력(agency_settlement_payments) — 구 '정산하기' 총액 upsert 를 대체.
//
// 통합 워크스페이스 (IA v2, Jin 확정 2026-08-12) — '대상(scope) × 화면(view)' 이 서로 독립인 2축이다:
//   대상(stlScope) = 헤더의 대상 칩(거래처 픽커)에서만 바뀐다. '' 전체 / 'a:' 청구 계정 / 'u:' 작업 단위.
//   화면(stlView)  = 세그먼트 [현황|장부|입금|정산서]. 세그먼트를 눌러도 대상은 절대 바뀌지 않는다.
//   매트릭스 — 전체×현황 = 미수 보드 / 전체×장부 = 전체 합산 장부 / a:×현황 = 집계 스트립 + 하위 업체 표
//             / u:·direct×현황 = 집계 스트립 / (대상)×장부 = 스트립 + 차량 장부 표
//             / (대상)×입금 = 입금 관리 / (대상)×정산서 = 정산서 보관함(임베드)
//   일계표는 화면 축이 아니라 '장부' 화면 **안의 탭**이다 (Jin 9/2 3~4차):
//   [월별 정산표 | 일별 | 주별] — 첫 탭이 기존 장부 표 그대로, 나머지는 일계표 그룹 뷰가
//   그 자리를 통째로 대체한다. 일계표도 **대상(거래처) 축을 따른다** — 장부와 같은
//   scopeParams/qsOf 좌표를 서버에 넘긴다 (구 '대행사 전체 고정' 규약은 폐기, Jin 9/2 4차).
// 상시 버튼 5개 — [대상 칩 ▾][‹월›][세그먼트][📄 발행][⋯]. 화면 이동 항목은 ⋯ 에서 전부 뺐다(중복 0).

// 정산 입력 프리셋 — 자주 쓰는 부가서비스/비고를 드롭다운으로. '기타(직접입력)' 선택 시 자유 텍스트.
export var EXTRA_SERVICE_PRESETS=['임시번호판 발급','자동차세 정산·대납','과태료·범칙금 대납','책임보험 해지대행','인감증명 발급','위임장·양도증명 작성','번호판 영치 해제','기타(직접입력)'];
// 장부 비고 프리셋 (앱 settlement_item_edit_screen.dart _kNotePresets 정합)
export var NOTE_PRESETS=['등재','등재만','이전','임판 번호판','번호판컷팅','주소변경','기타(직접입력)'];
var OTHER_OPT='기타(직접입력)';

// 프리셋 select + 기타 직접입력 콤보. 저장값이 프리셋에 없으면 자동 기타 모드(기존값 호환). 모달 재오픈 시 key 로 remount 권장.
export function PresetCombo(props){
  var presets=props.presets, v=props.value||'', is=props.is||{};
  var inList=presets.indexOf(v)>-1 && v!==OTHER_OPT;
  var manualInit=v!==''&&!inList;
  var [manual,setManual]=useState(manualInit);
  return (<div>
    <select style={is} value={manual?OTHER_OPT:(inList?v:'')} onChange={function(e){var s=e.target.value;if(s===OTHER_OPT){setManual(true);props.onChange('')}else{setManual(false);props.onChange(s)}}}>
      <option value="">선택...</option>
      {presets.map(function(p){return <option key={p} value={p}>{p}</option>})}
    </select>
    {manual&&<input style={{...is,marginTop:6}} placeholder={props.placeholder||'직접 입력'} value={v} onChange={function(e){props.onChange(e.target.value)}}/>}
  </div>);
}

// 부가서비스 카탈로그 칩 다중 선택 (W8 — 요청 미연결 정산 항목 전용, EXTRA_SERVICE_PRESETS 스피너 대체).
// 카탈로그 선택 + 자유 행을 RequestServicesEditor 와 같은 모양(rows 배열, selectedIds 는 rows 에서 파생)으로
// 관리 — 표(선택 N건) 에 카탈로그 선택도 함께 보이고, ×/칩 재클릭 둘 다로 제거된다.
// value(extraServiceNote) 를 ' / ' 로 split 해 카탈로그 이름과 일치하는 것은 칩 선택 행으로, 나머지는
// 자유 행으로 복원한다. 변경 시 이름만 다시 ' / ' 로 합쳐 onChange (메모 칸은 표 공용 UI 라 뜨지만 저장
// 대상이 아니다 — RequestServicesEditor 의 메모와 달리 이 콤보는 이름 목록만 필요하다).
// 모달 재오픈 시 key 로 remount 권장(PresetCombo 와 같은 관례).
export function ExtraServiceCombo(props){
  var catalog=props.catalog||[], v=props.value||'';
  var parts=v?v.split(' / ').map(function(s){return s.trim()}).filter(function(s){return s!==''}):[];
  var byName={}; catalog.forEach(function(c){byName[c.name]=c.id});
  var initRows=parts.map(function(p){ return byName[p]!==undefined?{catalogId:byName[p],name:p,memo:''}:{name:p,memo:''}; });
  var [rows,setRows]=useState(initRows);
  var emit=function(list){ props.onChange(list.map(function(r){return r.name}).filter(function(n){return (n||'').trim()!==''}).join(' / ')); };
  var toggleChip=function(c){
    setRows(function(prev){
      var existing=prev.filter(function(r){return r.catalogId===c.id})[0];
      var next=existing?prev.filter(function(r){return r.catalogId!==c.id}):prev.concat([{catalogId:c.id,name:c.name,memo:''}]);
      emit(next); return next;
    });
  };
  var addFree=function(){ setRows(function(prev){var next=prev.concat([{name:'',memo:''}]); emit(next); return next;}); };
  var setName=function(idx,val){ setRows(function(prev){var next=prev.map(function(r,i){return i===idx?Object.assign({},r,{name:val}):r}); emit(next); return next;}); };
  var setMemo=function(idx,val){ setRows(function(prev){return prev.map(function(r,i){return i===idx?Object.assign({},r,{memo:val}):r});}); };
  var removeRow=function(idx){ setRows(function(prev){var next=prev.filter(function(_,i){return i!==idx}); emit(next); return next;}); };
  var selectedIds=new Set(rows.filter(function(r){return r.catalogId}).map(function(r){return r.catalogId}));
  var tableRows=rows.map(function(r,idx){return {key:idx,name:r.name,memo:r.memo,editableName:!r.catalogId};});
  return (<ExtraServiceChipTable chips={catalog} selectedIds={selectedIds} onToggleChip={toggleChip}
    extraChip={{label:'직접 입력',onClick:addFree}} rows={tableRows} showFee={false} readOnly={false}
    onNameChange={setName} onMemoChange={setMemo} onRemove={removeRow}
    emptyText="칩을 눌러 부가서비스를 선택하세요"/>);
}

// 화면 정산입력 공용: VAT 미리보기 (발행형 가정 — 비발행 폼은 vat 을 0 으로 치환해서 쓴다).
// 서버는 constants.VAT_RATE 로 재계산(저장 시 정합). 단일 source.
//   과세 = 수수료 + 기타(과세) → vat / 재교부수수료 → reissueVat (세액이 두 칸이다, M1)
//   소계 = 등록세+증지+원부(legacy)+수수료+세액 + 재교부3칸 + 구 재교부(legacy) + 기타 + 기타(과세) + 부가서비스
//   → utils/cancelSettlement.computeManualItemized 와 같은 식. 어긋나면 저장 후 숫자가 튄다.
var SETTLE_VAT_RATE=0.1;
// extraTaxable — 요청 연결 항목(W8)의 부가서비스 중 '계산서 포함'분(재교부 제외) 합. 서버 extraServiceTaxableFee 와
// 같은 몫으로 세액 산정에만 더하고 소계에는 더하지 않는다(부가서비스 칸 금액과 중복 방지, B-1).
export var calcSettlementPreview=function(f,extraTaxable){
  var n=function(k){return parseInt(f[k])||0};
  var sf=n('serviceFee'), otx=n('otherTaxableFee'), rsf=n('reissueServiceFee');
  var etx=parseInt(extraTaxable)||0;
  var vat=Math.round((sf+otx+etx)*SETTLE_VAT_RATE);
  var reissueVat=Math.round(rsf*SETTLE_VAT_RATE);
  var subtotal=n('registrationTax')+n('stampDuty')+n('wonbuFee')+sf+vat
    +n('reissueFee')+n('reissueStampDuty')+rsf+reissueVat
    +otx+n('otherFee')+n('extraServiceFee');
  return {vat:vat,reissueVat:reissueVat,subtotal:subtotal};
};
// 폼 미리보기 실값 — 비발행이면 두 세액을 0 으로 치환하고 소계에서 뺀다 (서버 computeManualItemized 의 noIssue 분기와 동일).
export var settlementPreviewOf=function(f,issue,extraTaxable){
  var pv=calcSettlementPreview(f,extraTaxable);
  var vat=issue?pv.vat:0, rvat=issue?pv.reissueVat:0;
  return {vat:vat,reissueVat:rvat,subtotal:pv.subtotal-pv.vat-pv.reissueVat+vat+rvat};
};

var fmt=function(n){return(parseInt(n)||0).toLocaleString()};
// 장부 표기 — 0 은 '-'
var amt=function(n){return(parseInt(n)||0)===0?'-':fmt(n)};
// 분리 컬럼 도입(Wave 3) 이전 행은 등록세/증지/원부가 모두 0 이고 baseFee 에만 실비가 합산돼 있다.
// 그대로 그리면 컬럼 합이 소계와 어긋나므로 '미분류 실비'로 괄호 표기 (앱 utils/settlement_ledger.dart 정합).
export var unclassifiedCost=function(it){
  if(((it.registrationTax||0)+(it.stampDuty||0)+(it.wonbuFee||0))!==0)return 0;
  var rest=(it.baseFee||0)-(it.serviceFee||0)-(it.vatAmount||0);
  return rest>0?rest:0;
};
// 같은 날짜 항목 묶음 — 장부의 '합계'(일자 그룹 소계). 서버 정렬(date, id) 순서 보존.
var groupByDate=function(items){
  var order=[],buckets={};
  items.forEach(function(it){var k=it.date||'';if(!buckets[k]){buckets[k]=[];order.push(k)}buckets[k].push(it)});
  return order.map(function(k){return {date:k,items:buckets[k],subtotal:buckets[k].reduce(function(s,i){return s+(i.subtotal||0)},0)}});
};
var sumBy=function(items,key){return items.reduce(function(s,i){return s+(i[key]||0)},0)};
// 재교부 그룹 3분할(M1) 이전 행의 미분해 금액 = 구 재교부 + 구 원부. 소급 분해하지 않고
// 재교부수수료 칸에 괄호로 합계만 찍고 나머지 2칸은 '-' — utils/statementPdf.js legacyReissueSum 과 동일 규칙.
// 신규 행은 두 칸이 항상 0 이라 이 값이 0 → 새 3칸이 그대로 렌더된다.
var legacyReissueSum=function(it){return (parseInt(it.reissueFee,10)||0)+(parseInt(it.wonbuFee,10)||0)};
// 재교부증지 700 자동채움(reissueSvcPatch)은 입력 폼과 함께 SettlementItemDrawer.js 로 옮겼다.

// 장부 표의 텍스트 셀 — 모바일 전용(가로 스크롤 래퍼 안이라 접지 않고 한 줄로 둔다)
var nowrapCell={padding:'6px',fontSize:11,color:'#475569',whiteSpace:'nowrap'};
var nowrapCellSub={padding:'6px',fontSize:11,color:'#64748b',whiteSpace:'nowrap'};
var isSm={width:'100%',padding:'6px 10px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:13,outline:'none',boxSizing:'border-box'};
// 수기 추가 기본값 — 구 '원부'·'재교부비' 는 입력에서 뺐다(legacy 전용 컬럼, 신규 행은 0).
// 재교부증지는 0 으로 시작하고 재교부수수료를 입력하는 순간 700 이 자동으로 채워진다(reissueSvcPatch).
var ADD_ITEM_DEFAULT={date:'',carNo:'',carType:'',vinLast6:'',registrationTax:'15000',stampDuty:'2300',serviceFee:'0',
  reissueStampDuty:'0',reissueServiceFee:'0',otherFee:'0',otherTaxableFee:'0',extraServiceFee:'0',extraServiceNote:'',district:'',note:'',jobKind:'말소'};

// 거래처 스코프 (리빌드 R3) — '' 전체 / 'u:<unitId>' 작업 단위 / 'a:<accountId>' 청구 계정(브로커 취합)
var scopeUnitId=function(v){return (v||'').indexOf('u:')===0?(parseInt((v||'').slice(2),10)||0):0};
var scopeAccountId=function(v){return (v||'').indexOf('a:')===0?(parseInt((v||'').slice(2),10)||0):0};
// 미수 탭 / 관리자 대시보드 진입 요청은 구 인코딩('c:<companyId>' / 'w:<clientId>')으로 온다 → 신 인코딩 변환.
// w: 의 clientId 는 work_unit id 와 같은 값(R1 호환 컬럼)이라 그대로 u: 로 승격, c: 는 단위 역참조.
var convertLegacyScope=function(v,unitIdx){
  var s=v||'';
  if(s.indexOf('w:')===0)return 'u:'+(parseInt(s.slice(2),10)||0);
  if(s.indexOf('c:')!==0)return s;
  var cid=parseInt(s.slice(2),10)||0,hit='';
  Object.keys(unitIdx).forEach(function(uid){
    if(!hit&&cid>0&&(parseInt(unitIdx[uid].unit.companyId,10)||0)===cid)hit='u:'+uid;
  });
  return hit;
};

function CancelAgencySettlement(props){
  var user=props.user, showToast=props.showToast, isCancelAgency=props.isCancelAgency, active=props.active;
  var agencies=props.agencies||[], agencySearch=props.agencySearch, setAgencySearch=props.setAgencySearch;
  var openPdf=props.openPdf, focusAgency=props.focusAgency, onActivate=props.onActivate;
  var focusRef=useRef(null); // 처리한 진입 요청 — 같은 객체 재처리 방지
  // #cap-web 정산: 동시 작업 프레즌스 — {itemId: users[]}. users 는 **접속(소켓) 단위**라 본인 접속(sid)만
  // 빼고, 같은 계정의 다른 접속(PC↔모바일)은 "(PC)"/"(모바일)" 을 붙여 남긴다 (utils/carPresence).
  var [presenceByItem, setPresenceByItem] = useState({});
  // 구독 + 목록 요청을 한 채널로 묶는다 — CancelAgencySettlement(항상 마운트)와 CancelAgencyMalsoBox 가
  // 같은 소켓 싱글턴(settlement:presence)을 각자 구독하므로, 스냅샷을 "이 채널이 요청한 id" 로
  // 거르는 주체가 팩토리다(utils/carPresence). 화면은 걸러진 결과만 반영한다.
  var presenceChanRef = useRef(null);
  if (!presenceChanRef.current) presenceChanRef.current = createPresenceChannel();
  var presenceChan = presenceChanRef.current;
  var currentUserId = user && user.id;
  // 내 socket.id 는 렌더당 1회만 읽는다 — 행마다 부르면 긴 장부에서 getSocket() 이 행 수만큼 불린다.
  var mySid = presenceMySid();
  var othersForItem = function(itemId){
    return presenceOthersLabeled(presenceByItem[itemId]||[], mySid, currentUserId);
  };
  // 서버 미배포/구 서버면 소켓 이벤트가 안 와 presenceByItem 은 항상 빈 채로 유지된다(표시 없음, 에러 없음).
  useEffect(function(){
    var unsub = presenceChan.subscribe(function(evt){
      if(evt.type==='presence'){
        var p=evt.payload||{};
        if(!p.itemId)return;
        setPresenceByItem(function(prev){var n=Object.assign({},prev);n[p.itemId]=p.users||[];return n});
      } else if(evt.type==='snapshot'){
        // 팩토리가 이 채널이 요청한 id 로 걸러 넘긴다 — 담겨 온 것만 반영하고, 값이 비었으면
        // "이제 아무도 없다"로 지운다 (프레즌스 3종 4화면 공통 코드).
        var snap=evt.payload||{};
        setPresenceByItem(function(prev){
          var n=Object.assign({},prev);
          Object.keys(snap).forEach(function(id){if(snap[id]&&snap[id].length)n[id]=snap[id];else delete n[id];});
          return n;
        });
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  // 다른 사용자 작업 중 표시 — 장부 표 행 (CancelAgency.js reqPresenceBadge 와 동형)
  var itemPresenceBadge = function(itemId){
    return <PresenceDot users={othersForItem(itemId)} reserve />;
  };
  // 드로어 상단 배너 (마크업은 components/PresenceBanner.js 한 벌)
  var itemPresenceBanner = function(itemId){
    return <PresenceBanner users={othersForItem(itemId)} target="이 항목" />;
  };
  // 모바일(≤768px) 반응형 — 데스크탑 렌더는 그대로, isMobile 분기로만 좁은 화면 대응.
  var isMobile=useIsMobile();
  // .dash-content 패딩 (모바일 12 / 데스크탑 24) — sticky top 음수 상쇄 + boxShadow 커버 폭의 단일 원천.
  var pad=isMobile?12:24;
  var now2=new Date();
  // 로컬(KST) 기준 YYYY-MM-DD — toISOString 은 UTC 라 오전 9시 전엔 전날/전월로 밀린다
  var ymdLocal=function(d){return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0')};
  var [stlAgencyId, setStlAgencyId] = useState('');
  var [stlMonth, setStlMonth] = useState(ymdLocal(now2).slice(0,7));
  var [stlData, setStlData] = useState(null);
  // 조회 결과가 바뀔 때마다(재조회 포함) 그 항목들의 프레즌스를 요청 — 배지가 새 목록을 따라간다.
  // active 를 deps 에 포함 — 탭 복귀 시 재요청(백그라운드에 있는 동안 놓친 갱신을 따라잡는다).
  var stlItemIdsKey=(stlData&&Array.isArray(stlData.items))?stlData.items.map(function(i){return i.id}).join(','):'';
  useEffect(function(){
    var ids=(stlData&&Array.isArray(stlData.items))?stlData.items.map(function(i){return i.id}):[];
    // 목록에서 빠진 id 정리는 팩토리 몫 — requestList 가 직전 요청과 비교해 빈 users 로 통지한다.
    presenceChan.requestList(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[stlItemIdsKey, active]);
  var [stlLoading, setStlLoading] = useState(false); // 조회 중 — 기존 데이터를 지우지 않고 dim (월 이동 깜빡임 제거)
  var [stlScope, setStlScope] = useState(''); // 대상 축 — '' | 'u:<unitId>' | 'a:<accountId>'
  var [stlView, setStlView] = useState('overview'); // 화면 축 — 'overview'|'ledger'|'payments'|'statements' (대상과 독립)
  // 장부 화면 안 탭 — 'ledger'(기존 월별 정산표) | 'day' | 'week' | 'year'(일계표 그룹 뷰).
  // stlView 축과 별개인 로컬 상태다(navSnap 비대상) — 거래처·월을 바꿔도 보던 탭을 유지한다.
  var [ledgerTab, setLedgerTab] = useState('ledger');
  var [agencyHi, setAgencyHi] = useState(-1);  // admin 업체 검색 하이라이트 (-1 = 이동 전)
  var [accounts, setAccounts] = useState([]);         // GET /billing-accounts (청구 계정)
  var [unitsByAcc, setUnitsByAcc] = useState({});     // {accountId: [work_units]}
  var [scopeReady, setScopeReady] = useState(false);  // 계정/단위 로드 완료 (진입 요청 변환 대기)
  var [pendingScope, setPendingScope] = useState(''); // 로드 전 도착한 진입 스코프 (구 인코딩 가능)
  var [drawer, setDrawer] = useState(null);           // 원장 드로어 {accountId, unitId, label}
  var [issued, setIssued] = useState(null);           // 방금 발행한 정산서 {id, shareUrl, name}
  var [issuing, setIssuing] = useState(false);
  var accRef=useRef('');                              // 계정 목록을 로드한 대행사 id + 월 (monthCharged 가 월 의존)
  var [taxInv, setTaxInv] = useState(null); // 세금계산서 발행 상태 {record, computed, issueTaxInvoice}
  var [adjustments, setAdjustments] = useState([]); // 수기 조정 (GET /settlement-adjustments) — loadStl 과 같은 좌표
  var [impAdjTotal, setImpAdjTotal] = useState(0); // 같은 응답의 엑셀 이관 조정 합 (목록 밖, 스트립 가산분)
  var adjSeqRef = useRef(0);                        // 마지막 조정 조회만 반영 (스코프 연타 시 이전 응답 무시)
  var stlSeqRef = useRef(0), taxSeqRef = useRef(0), accSeqRef = useRef(0);   // 장부·계산서·계정도 같은 규칙 — 월 ‹ › 연타 시 늦게 온 옛 달 응답이 새 달 제목 아래 그려지던 문제 (10/22)
  var [showAdjust, setShowAdjust] = useState(false);
  var [showAddItem, setShowAddItem] = useState(false);
  var [addItemForm, setAddItemForm] = useState(ADD_ITEM_DEFAULT);
  var [reorderBusy, setReorderBusy] = useState(false); // 순서 저장 진행 중 — ▲▼/드래그 연타 차단
  var [dragId, setDragId] = useState(0);               // 드래그 중인 행 id (데스크톱 DnD)
  var [dropId, setDropId] = useState(0);               // drop 대상 행 id (상단 강조선)
  var dragRef = useRef(null);                          // {id,date} — dragover/drop 이 동기로 읽는 드래그 원본
  // 작업 유형 프리셋 (GET /agency-work-types) — 수기 추가/수정 모달의 입력 보조.
  // 서버가 아직 없거나 실패하면 빈 배열 → 셀렉트 자체를 숨긴다 (기존 동작 그대로).
  var [workTypes, setWorkTypes] = useState([]);
  // 부가서비스 카탈로그 (GET /cancel-agencies/:id/extra-services) — 수기(미연결) 항목의 칩 다중 선택 보조.
  // 서버가 아직 없거나 실패하면 빈 배열 → 칩 줄 자체가 안 뜬다 (workTypes 와 같은 폴백).
  var [extraCatalog, setExtraCatalog] = useState([]);
  var [editItem, setEditItem] = useState(null);
  // B-1 — 연결 항목(RequestServicesEditor) rows 라이브 반영용 과세표준 가산분. editItem 이 바뀔 때마다 리셋.
  var [linkedExtraTaxable, setLinkedExtraTaxable] = useState(0);
  // 드로어 열림/닫힘 — editItem?.id 가 바뀔 때마다 enter/leave. 모든 닫기 경로(취소·✕·Esc·저장·삭제)가
  // setEditItem(null) 을 거치므로 이 effect 하나로 cleanup 이 보장된다(개별 닫기 핸들러에 leave 를 흩뿌리지 않는다).
  useEffect(function(){
    if(!editItem||!editItem.id) return undefined;
    var id = editItem.id;
    presenceEnter(id);
    return function(){ presenceLeave(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[editItem&&editItem.id]);
  var [payForm, setPayForm] = useState({date:ymdLocal(now2),amount:'',memo:''});
  var [payScope, setPayScope] = useState(null); // #303 — 입금 귀속 거래처. null = 조회 스코프 따라감, ''=(미지정), 'a:/u:'=명시 선택
  var [editPay, setEditPay] = useState(null);   // #304 — 이력 행 전체 편집 {id, scope, date, amount, memo}
  var [malsoBusy, setMalsoBusy] = useState(null);     // 업로드 진행 {done,total} — 진행 중 재클릭 차단
  var [malsoResults, setMalsoResults] = useState(null); // 업로드 결과 요약 모달 rows
  var malsoInputRef=useRef(null);
  var [pickerOpen, setPickerOpen] = useState(false);
  var [subQ, setSubQ] = useState('');                     // 대행사 현황 — 하위 업체 검색 필터
  var [subSort, setSubSort] = useState({key:'balanceEnd',dir:'desc'}); // 하위 표 정렬 (헤더 클릭)  // 거래처 픽커 오버레이 (대상 칩이 트리거)
  var [hdrSlotL, setHdrSlotL] = useState(null);       // 헤더 좌측 슬롯 (#dash-header-slot-left) — 밑줄 탭 (컨트롤·액션은 본문 툴바 줄, Jin 8/26)
  var [menuOpen, setMenuOpen] = useState(false);      // ⋯ 메뉴 (PDF/수기/말소증/세금계산서)
  var [stripHelp, setStripHelp] = useState('');       // 요약 스트립 ⓘ 설명 — 열린 칸 키 (한 번에 하나)
  var [showMonthInput, setShowMonthInput] = useState(false); // 월 라벨 클릭 시 month input 노출
  var [boardKey, setBoardKey] = useState(0);          // 보드 재조회 트리거 (입금/이월 변경 후)
  var payAmtRef=useRef(null);   // 입금 금액 입력
  var payHeadRef=useRef(null);  var [payHeadH,setPayHeadH]=useState(0); // 입금 관리 상단 고정 블록 높이 (표 헤더 sticky 오프셋)

  // ── 미수 표시 규칙 (통일안 v2, Jin 확정 2026-08-12) ────────────────────────
  // 화면의 모든 미수 숫자는 '조회 월 시점' 하나로 통일: 미수 = 그 달 opening(지난달 청구·이월 포함) − 그 달 입금.
  // 스트립·하위 리스트·보드 행·패널이 전부 같은 수를 가리킨다. 그 달 청구는 별도 세그/꼬리표(현재 달만 '예상').
  // 예외 (Jin 10/20): 요약 스트립·하위 업체 표·거래처 픽커/콤보는 엑셀 청구서 꼬리 순서 — 미수 (그 달 말) = opening + 그 달 청구 − 그 달 입금.
  //   YK 처럼 그 달 청구서에 그 달 청구분 입금을 적는 거래처가 선수금으로 보이지 않게.
  var isCurMonth=stlMonth===ymdLocal(now2).slice(0,7);
  // 그 달 말 미수 — monthOpening/monthCharged/monthPaid 미제공(구 서버 · 월 없는 /units 재조회)이면 서버 balance(전 기간 원장 합) 폴백.
  var monthEndBal=function(o){
    if(o&&o.monthOpening!==undefined&&o.monthOpening!==null)
      return (parseInt(o.monthOpening,10)||0)+(parseInt(o.monthCharged,10)||0)-(parseInt(o.monthPaid,10)||0);
    return parseInt(o&&o.balance,10)||0;
  };

  // 계정/단위 색인 — 스코프 매핑(단위 → companyId/clientId), 드릴다운, 원장 드로어의 계정 역참조 원천.
  var accountById={};accounts.forEach(function(a){accountById[a.id]=a});
  var unitIdx={};var unitByCompany={};Object.keys(unitsByAcc).forEach(function(aid){
    (unitsByAcc[aid]||[]).forEach(function(u){
      unitIdx[u.id]={unit:u,accountId:parseInt(aid,10)||0};
      var ucid=parseInt(u.companyId,10)||0;
      if(ucid&&!unitByCompany[ucid])unitByCompany[ucid]=u.id; // 회사 연결 단위 역색인 (#304 입금 행 → 콤보 초기값)
    });
  });
  // 거래처 스코프 선택 데이터 — 거래처 픽커(오버레이)와 입금 귀속 콤보가 같은 것을 쓴다.
  // 표시 잔액은 그 달 말 미수 (스트립 '미수 (그 달 말)'과 같은 식).
  var scopeData={groups:[],directs:[]};
  accounts.forEach(function(a){
    if(!a.active)return; // 재배치(rebroker)로 비활성화된 옛 direct 껍데기 — 선택 목록에서 제외 (이력 조회는 accountById/unitIdx 로 유지)
    var us=unitsByAcc[a.id]||[];
    if(a.kind==='broker'){
      scopeData.groups.push({account:{id:a.id,kind:a.kind,name:a.name,balance:monthEndBal(a),unitsCount:(a.unitsCount!=null?a.unitsCount:us.length)},
        units:us.map(function(u){return {id:u.id,name:u.name,balance:monthEndBal(u)}})});
    }else{
      scopeData.directs.push({accountId:a.id,unitId:(us[0]&&us[0].id)||0,name:a.name,balance:monthEndBal(a)});
    }
  });
  var stlUnitId=scopeUnitId(stlScope), stlAccountId=scopeAccountId(stlScope);
  var curUnit=stlUnitId&&unitIdx[stlUnitId]?unitIdx[stlUnitId].unit:null;
  var curUnitAccId=stlUnitId&&unitIdx[stlUnitId]?unitIdx[stlUnitId].accountId:0;
  // 회사 연결 단위는 기존 companyId 갈래, 미연결(walk-in) 단위는 clientId 갈래 — 호환 API 규약 그대로.
  var stlCompanyId=curUnit?(parseInt(curUnit.companyId,10)||0):0;
  var stlClientId=stlUnitId&&!stlCompanyId?stlUnitId:0;
  var scopeAcc=accountById[stlAccountId||curUnitAccId]||null; // 현재 스코프가 속한 청구 계정
  var ledgerAccId=stlAccountId||curUnitAccId;                 // 원장 드로어 대상 계정 (전체 스코프면 0)
  // 정산서 발행 단위 = 청구 계정. 계정 스코프(a:) 또는 direct 계정의 단위 스코프에서만 노출.
  var issueAcc=stlAccountId?scopeAcc:((scopeAcc&&scopeAcc.kind==='direct')?scopeAcc:null);

  // 스코프 → 조회 파라미터. 계정(a:)은 신 accountId 취합 조회, 단위(u:)는 기존 companyId/clientId 호환.
  var scopeParams=function(sc){
    var aid=scopeAccountId(sc), uid=scopeUnitId(sc);
    if(aid)return {accountId:aid,companyId:0,clientId:0};
    if(uid){
      var u=unitIdx[uid]?unitIdx[uid].unit:null;
      var cid=u?(parseInt(u.companyId,10)||0):0;
      return {accountId:0,companyId:cid,clientId:cid?0:uid};
    }
    return {accountId:0,companyId:0,clientId:0};
  };
  var qsOf=function(p){return p.accountId?('?accountId='+p.accountId):(p.companyId?('?companyId='+p.companyId):(p.clientId?('?clientId='+p.clientId):''))};

  // 세금계산서 발행 상태 조회 — 발행형 거래처(companyId>0) 선택 시. 미선택 시 초기화.
  var loadTaxInv = function(agencyId, month, companyId){
    var seq=++taxSeqRef.current;
    if(!agencyId || !companyId){setTaxInv(null);return;}
    authFetch(API+'/settlement-tax-invoice/'+agencyId+'/'+companyId+'/'+month)
      .then(function(r){return r.json()}).then(function(d){if(seq===taxSeqRef.current)setTaxInv(d&&typeof d==='object'?d:null)}).catch(function(){if(seq===taxSeqRef.current)setTaxInv(null)});
  };
  // 수기 조정 조회 — loadStl 이 함께 부른다(재조회·실시간 갱신·스코프/월 이동 전부 loadStl 경유).
  var loadAdjustments = function(agencyId, month, sc){
    var seq=++adjSeqRef.current;
    fetchAdjustments(agencyId, month, scopeUnitId(sc), scopeAccountId(sc)).then(function(r){if(seq===adjSeqRef.current){setAdjustments(r.adjustments);setImpAdjTotal(r.importTotal)}})
      .catch(function(){if(seq===adjSeqRef.current)showToast('수기 조정 조회 실패 — 다시 조회해 주세요','err')});
  };
  // 정산 조회 — 스코프는 계정(accountId 취합) / 단위(companyId·clientId) / 전체 중 하나.
  var loadStl = function(agencyId, month, sc){
    if(!agencyId) return;
    var p = scopeParams(sc);
    var seq=++stlSeqRef.current;
    setStlLoading(true);
    authFetch(API+'/settlement-items/'+agencyId+'/'+month+qsOf(p)).then(function(r){return r.json()}).then(function(d){
      if(seq!==stlSeqRef.current)return;
      setStlLoading(false);
      // 에러 응답(401 인증 필요 등)을 stlData 로 넣으면 렌더가 stlData.agency.* 에서 크래시 — 가드 후 토스트만
      if(!d||!d.agency){showToast((d&&d.error)||'조회 실패','err');return;}
      setStlData(d);
    }).catch(function(){if(seq!==stlSeqRef.current)return;setStlLoading(false);showToast('조회 실패','err')});
    loadTaxInv(agencyId, month, p.companyId);
    loadAdjustments(agencyId, month, sc);
  };
  // 현 스코프 계정의 단위 잔액 갱신 — 입금/명세 변경 후 하위 업체 미수 표시 정합 (1 요청).
  var reloadScopeUnits = function(accId){
    if(!accId) return;
    authFetch(API+'/billing-accounts/'+accId+'/units').then(function(r){return r.json()}).then(function(us){
      if(!Array.isArray(us)) return;
      setUnitsByAcc(function(prev){var n=Object.assign({},prev);n[accId]=us;return n});
    }).catch(function(){});
  };
  // 재조회 — 조회 스코프 + 계정/단위 잔액 + 미수 보드(전체 스코프에서 마운트된 경우).
  // 계정 목록 전체를 다시 받는다 (1요청) — 단위만 갱신하면 월 흐름(monthOpening/monthCharged/monthPaid)이 빠져 미수 표시가 어긋난다.
  // (A3-2 이후 admin/doc_agency 도 같은 색인을 쓴다. 대행사 미선택이면 부분 갱신 폴백.)
  var reloadStl = function(){
    loadStl(stlAgencyId,stlMonth,stlScope);
    if(stlAgencyId)loadAccounts(stlAgencyId,stlMonth); else reloadScopeUnits(ledgerAccId);
    setBoardKey(function(k){return k+1});
  };
  // 핸들러는 매 렌더 최신본으로 갈아끼우고, 구독은 1회만 (CancelAgencyMalsoBox.js ocrRef 관례 + stale closure 방지)
  var reloadStlRef=useRef(reloadStl); reloadStlRef.current=reloadStl;
  var stlAgencyIdRef=useRef(stlAgencyId); stlAgencyIdRef.current=stlAgencyId;
  var stlMonthRef=useRef(stlMonth); stlMonthRef.current=stlMonth;
  // CancelAgencySettlement 는 항상 마운트돼 있다(다른 탭에 있어도) — active 가드 없이 재조회하면
  // 백그라운드 탭에서도 계속 GET 을 쏜다. 실제 화면에 보일 때만 반응.
  var activeRef=useRef(active); activeRef.current=active;
  // 실시간 갱신 — settlement:updated {agencyId,month,kind,id} 는 정산 쓰기마다 온다.
  // 현재 조회 중인 대행사·월과 일치할 때만(+ 이 탭이 활성일 때만) 재조회(300ms debounce), connect 시 1회 재조회.
  useEffect(function(){
    var sock=getSocket();
    if(!sock)return undefined;
    var t=null;
    var onUpdated=function(d){
      if(!activeRef.current)return;
      if(!d||String(d.agencyId||'')!==String(stlAgencyIdRef.current||'')||(d.month||'')!==(stlMonthRef.current||''))return;
      if(t)clearTimeout(t);
      t=setTimeout(function(){reloadStlRef.current();},300);
    };
    var onConnect=function(){if(!activeRef.current)return;reloadStlRef.current();};
    sock.on('settlement:updated',onUpdated);
    sock.on('connect',onConnect);
    return function(){
      if(t)clearTimeout(t);
      try{sock.off('settlement:updated',onUpdated);}catch(e){}
      try{sock.off('connect',onConnect);}catch(e){}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  // 청구 계정 + 하위 단위 로드 (콤보 데이터·스코프 매핑·드릴다운의 원천).
  // includeUnits=1 로 1요청 원자 로드 — 계정/단위가 함께 도착해 '하위 업체 없음' 플래시가 없다.
  // 구 서버(units 미동봉) 응답이면 계정당 /units 6개씩 배치 로드로 폴백.
  // month = 조회 월 — 픽커/콤보·하위 업체 표는 그 달 말 미수(monthOpening + monthCharged − monthPaid)를 쓴다 (Jin 10/20).
  var loadAccounts = function(agencyId, month){
    var seq=++accSeqRef.current;
    setScopeReady(false);
    if(!agencyId){setAccounts([]);setUnitsByAcc({});setScopeReady(true);return;}
    var m=month||ymdLocal(new Date()).slice(0,7);
    authFetch(API+'/billing-accounts?agencyId='+agencyId+'&includeUnits=1&month='+m).then(function(r){return r.json()}).then(function(list){
      if(seq!==accSeqRef.current)return;
      var accs=Array.isArray(list)?list:[];
      setAccounts(accs);
      if(accs.length===0||Array.isArray(accs[0].units)){
        var map={};accs.forEach(function(a){map[a.id]=a.units||[]});
        setUnitsByAcc(map);setScopeReady(true);return;
      }
      var fmap={},i=0;
      var step=function(){
        if(seq!==accSeqRef.current)return;
        if(i>=accs.length){setUnitsByAcc(fmap);setScopeReady(true);return;}
        var slice=accs.slice(i,i+6);i+=6;
        Promise.all(slice.map(function(a){
          return authFetch(API+'/billing-accounts/'+a.id+'/units').then(function(r){return r.json()})
            .then(function(us){fmap[a.id]=Array.isArray(us)?us:[]}).catch(function(){fmap[a.id]=[]});
        })).then(step);
      };
      step();
    }).catch(function(){if(seq!==accSeqRef.current)return;setAccounts([]);setUnitsByAcc({});setScopeReady(true)});
  };
  // 일별 마감 요약(stlDate/stlSummary/loadDaily, GET /settlement-daily)은 2026-08-07 제거 (Jin 결정)
  // — 장부 표의 날짜별 소계로 대체. 백엔드 endpoint 는 보존.

  // 탭 진입 시 조회 — 단일 effect 로 통합(진입 요청과 자동조회가 겹쳐 이중 요청되는 것 방지).
  // 진입 요청(focusAgency: 관리자 대시보드 / 미수 탭 행 클릭)이 있으면 그 대행사·거래처 스코프를 적용,
  // 없으면 cancel_agency 본인 대행사 자동 조회.
  useEffect(function(){
    if(!active) return;
    var aid=stlAgencyId, sc=stlScope, pend='';
    if(focusAgency && focusAgency.id && focusAgency!==focusRef.current){
      focusRef.current=focusAgency;
      aid=String(focusAgency.id);
      setStlAgencyId(aid);
      // 진입 스코프는 구 인코딩('c:'/'w:')일 수 있어 계정/단위 로드 완료 후 변환한다 (아래 effect).
      pend=focusAgency.scope||'';
      sc='';
      setStlScope('');
      setPendingScope(pend);
      // 진입 요청은 주 화면(전체 × 현황)으로 착지 — 열려 있던 오버레이/발행 안내도 함께 정리한다.
      setStlView('overview');setDrawer(null);
      setIssued(null);setMenuOpen(false);setEditPay(null);
    }else if(isCancelAgency && user && user.agencyId){
      aid=String(user.agencyId);
      if(stlAgencyId!==aid) setStlAgencyId(aid);
    }
    if(!aid) return;
    // 대행사 또는 월이 바뀌면 재로드 — 그 달 시점 흐름(monthOpening/monthPaid)이라 월별 재조회 필요.
    var accKey=aid+'|'+stlMonth;
    // A3-2 — admin/doc_agency 갈래도 계정/단위 색인을 로드한다. 색인이 비면 입금 행의 거래처 콤보가
    // (미지정)으로 보이고, 그대로 저장하면 귀속이 날아갔다 (관리자만 밟던 결함).
    if(accRef.current!==accKey){accRef.current=accKey;loadAccounts(aid,stlMonth);}
    if(pend) return; // 변환 후 조회
    loadStl(aid, stlMonth, sc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[active, isCancelAgency, stlMonth, focusAgency]);

  // 진입 요청 스코프 → 계정/단위 로드 완료 후 신 인코딩으로 변환 + 조회.
  useEffect(function(){
    if(!active || !pendingScope) return;
    if(!scopeReady) return; // 색인 로드 후 변환 (A3-2 이후 admin 갈래도 색인을 로드한다)
    var v=convertLegacyScope(pendingScope,unitIdx);
    var aid=stlAgencyId||((isCancelAgency&&user&&user.agencyId)?String(user.agencyId):'');
    setPendingScope('');
    setStlScope(v);
    if(!v) showToast('거래처를 찾지 못해 전체로 조회합니다','err');
    loadStl(aid, stlMonth, v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[active, pendingScope, scopeReady]);

  // ── 화면 상태 ↔ 브라우저 히스토리 (뒤로가기) ─────────────────────────────
  var navRef=useRef('');        // 마지막으로 히스토리에 심은 스냅샷 키
  var restoreRef=useRef(false); // popstate 복원 중 — 이 사이클은 push 하지 않고 탭 복귀 리셋도 건너뛴다

  // 탭 복귀(active false→true) — 오버레이(드로어/메뉴/픽커)만 닫고 대상·월·화면은 그대로 둔다.
  // 4개 view 가 모두 대등한 정식 화면이라 '주 화면으로 리셋'할 이유가 없다 (IA v2 ⑦).
  var prevActiveRef=useRef(false);
  useEffect(function(){
    var was=prevActiveRef.current;
    prevActiveRef.current=!!active;
    if(!active||was)return;
    if(restoreRef.current)return; // 뒤로가기가 정산 탭을 되살린 경우 — 복원 스냅샷을 지우지 않는다
    setDrawer(null);setMenuOpen(false);setPickerOpen(false);
  },[active]);

  // 헤더 좌측 슬롯(#dash-header-slot-left) — Dashboard 가 그리는 DOM. 탭 활성화 시점에 잡는다(없으면 포털 생략).
  useEffect(function(){
    setHdrSlotL(document.getElementById('dash-header-slot-left')||null);
  },[active]);
  // 개별 거래처에서 '현황' 상태로 남는 것 방지 — 장부로 자동 착지 (보드 행 클릭 포함)
  useEffect(function(){
    if(!scopeReady)return;
    if(stlView==='overview'&&stlScope&&!(stlAccountId&&scopeAcc&&scopeAcc.kind==='broker')){
      setStlView('ledger');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[scopeReady,stlView,stlScope,stlAccountId,scopeAcc&&scopeAcc.kind]);


  // 대상 선택 (픽커 / 보드 행 / 브로커 하위 드릴다운 공용) — 대상 축만 바꾼다. 화면(stlView)은 유지.
  var pickScope=function(v){
    var sc=v||'';
    var aid=stlAgencyId||((isCancelAgency&&user&&user.agencyId)?String(user.agencyId):'');
    setStlScope(sc);
    setIssued(null);
    setPayScope(null); // 입금 귀속 콤보는 조회 스코프 추종으로 복귀
    setEditPay(null);
    setMenuOpen(false);
    // 대행사(브로커 계정) 선택은 항상 '현황' 으로 — 하위 업체 표가 첫 화면 (Jin 8/12)
    if(sc.indexOf('a:')===0){
      var _acc=accountById[parseInt(sc.slice(2),10)||0];
      if(_acc&&_acc.kind==='broker')setStlView('overview');
    }
    setSubQ('');
    loadStl(aid, stlMonth, sc);
  };
  // 월 이동 — 스트립/장부 모두 조회 월 기준. effect(deps: stlMonth) 가 재조회한다.
  var shiftMonth=function(delta){
    var m=/^(\d{4})-(\d{2})$/.exec(stlMonth||'');
    if(!m)return;
    var d=new Date(parseInt(m[1],10), parseInt(m[2],10)-1+delta, 1);
    var next=d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0');
    // stlData 를 비우지 않는다 — 이전 달 표가 잠깐 남고 dim 되는 편이 통째 깜빡임보다 낫다(A2).
    setStlMonth(next);setMenuOpen(false);
  };
  // 입금 관리 상단 블록(타이틀+폼) 높이 — 표 헤더 sticky top 계산 (리사이즈/데이터 변화 대응)
  useEffect(function(){
    if(stlView!=='payments')return;
    var el=payHeadRef.current;
    if(!el)return;
    var set=function(){if(payHeadRef.current)setPayHeadH(payHeadRef.current.offsetHeight)};
    set();
    window.addEventListener('resize',set);
    return function(){window.removeEventListener('resize',set)};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[stlView,stlData]);
  var monthLabel=function(){
    var m=/^(\d{4})-(\d{2})$/.exec(stlMonth||'');
    return m?(m[1]+'년 '+parseInt(m[2],10)+'월'):(stlMonth||'-');
  };

  // ── 뒤로가기 동기화 ──────────────────────────────────────────────────────
  // URL 은 건드리지 않는다 (쿼리 오염 + CancelAgency 의 useLocation highlightId 충돌 방지) —
  // history state 에만 스냅샷(stlNav)을 싣는다. react-router 가 쓰는 state 필드(usr/key/idx)는
  // 보존하고 push 시 idx 만 1 늘려 라우터의 delta 계산을 깨지 않는다.
  // 오버레이(드로어/메뉴/모달)는 히스토리 대상이 아니다.
  // 스냅샷 = {대상, 월, 화면} 3축 (IA v2 — 구 pay/arc/all 플래그를 view 하나로 대체)
  var navSnap={t:'stl',scope:stlScope,month:stlMonth,view:stlView};
  var navKeyOf=function(s){return (s.scope||'')+'|'+(s.month||'')+'|'+(s.view||'overview')};
  var navKey=navKeyOf(navSnap);
  useEffect(function(){
    if(navRef.current===navKey)return;
    var first=navRef.current==='';
    navRef.current=navKey;
    if(restoreRef.current){restoreRef.current=false;return;} // 복원분은 이미 히스토리에 있다
    var st=Object.assign({},window.history.state,{stlNav:navSnap});
    // 최초 마운트(스냅샷 심기) / 백그라운드(다른 탭에 있을 때) 변경은 새 항목을 만들지 않는다
    if(first||!active){window.history.replaceState(st,'');return;}
    var idx=(window.history.state&&typeof window.history.state.idx==='number')?window.history.state.idx:0;
    st.idx=idx+1;
    window.history.pushState(st,'');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[navKey,active]);
  // popstate — 우리가 심은 스냅샷이면 그 화면으로 되돌린다. 정산 탭 밖이면 탭부터 복귀시킨다.
  useEffect(function(){
    var h=function(e){
      var s=e.state&&e.state.stlNav;
      if(!s||s.t!=='stl')return;
      if(navKeyOf(s)===navKey){if(!active&&onActivate)onActivate();return;}
      restoreRef.current=true;
      setStlScope(s.scope||'');
      // 구 스냅샷(pay/arc/all)은 view 가 없어 현황으로 착지. 폐지된 'journal'(→ 장부 화면 인라인 패널)은 장부로.
      setStlView(s.view==='journal'?'ledger':(s.view||'overview'));
      setMenuOpen(false);setEditPay(null);setDrawer(null);setIssued(null);
      var aid=stlAgencyId||((isCancelAgency&&user&&user.agencyId)?String(user.agencyId):'');
      // 월이 바뀌면 조회 effect(deps: stlMonth)가 재조회하므로 여기서 중복 요청하지 않는다.
      if((s.month||'')!==stlMonth)setStlMonth(s.month||stlMonth);
      else loadStl(aid,stlMonth,s.scope||'');
      if(!active&&onActivate)onActivate();
    };
    window.addEventListener('popstate',h);
    return function(){window.removeEventListener('popstate',h)};
  });

  // ── 작업 유형 프리셋 ──────────────────────────────────────────────────────
  // 마스터: CancelAgencyWorkTypes.js (말소대행 '작업 유형' 탭). 유형은 폼 입력 보조일 뿐이라
  // 저장 payload 는 무변경 — workTypeId 는 폼 state 안에만 있고 settlementItemBody / saveEditItem 의
  // 명시 키 목록에 들어가지 않으므로 서버로 나가지 않는다.
  useEffect(function(){
    var aid=stlAgencyId||((isCancelAgency&&user&&user.agencyId)?String(user.agencyId):'');
    if(!active||!aid){setWorkTypes([]);return;}
    authFetch(API+'/agency-work-types'+(isCancelAgency?'':('?agencyId='+aid)))
      .then(function(r){if(!r.ok)throw new Error('HTTP '+r.status);return r.json()})
      .then(function(d){setWorkTypes(Array.isArray(d)?d:[])})
      .catch(function(e){console.error('[work-types] 프리셋 조회 실패',e);setWorkTypes([])});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[active,stlAgencyId,isCancelAgency]);

  // 부가서비스 카탈로그 (W8) — 요청 미연결 정산 항목 칩 다중 선택. 공개 목록(GET /cancel-agencies/:id/extra-services,
  // 이름만·fee 없음) — RequestServicesEditor 가 요청-연결 항목에 쓰는 것과 같은 endpoint.
  useEffect(function(){
    var aid=stlAgencyId||((isCancelAgency&&user&&user.agencyId)?String(user.agencyId):'');
    if(!active||!aid){setExtraCatalog([]);return;}
    authFetch(API+'/cancel-agencies/'+aid+'/extra-services')
      .then(function(r){if(!r.ok)throw new Error('HTTP '+r.status);return r.json()})
      .then(function(d){setExtraCatalog(Array.isArray(d)?d:[])})
      .catch(function(e){console.error('[extra-services] 카탈로그 조회 실패',e);setExtraCatalog([])});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[active,stlAgencyId,isCancelAgency]);

  // 유형 선택 → 폼 자동 채움. null 금액은 "기본값 사용" 이므로 건드리지 않고,
  // 0 은 "0원 청구" 라 그대로 채운다. 비고는 이미 입력된 값이 있으면 덮지 않는다.
  // 채운 뒤에도 각 필드는 자유 편집 가능하고, 선택을 되돌려도(선택 안 함) 값은 되돌리지 않는다.
  var workTypePatch=function(id, form){
    var patch={workTypeId:String(id||'')};
    var wt=workTypes.filter(function(w){return String(w.id)===String(id)})[0];
    if(!wt) return patch;
    if(wt.serviceFee!==null&&wt.serviceFee!==undefined) patch.serviceFee=String(wt.serviceFee);
    if(wt.registrationTax!==null&&wt.registrationTax!==undefined) patch.registrationTax=String(wt.registrationTax);
    if(wt.stampDuty!==null&&wt.stampDuty!==undefined) patch.stampDuty=String(wt.stampDuty);
    if(!String(form.note||'').trim()) patch.note=wt.name;
    return patch;
  };
  var workTypeField=function(form, apply, inStyle){
    if(!workTypes.length) return null;
    // 드로어 '비고' 옆 프리셋 — 고르면 단가 3칸 + (비어 있으면) 비고가 채워진다. 라벨은 드로어 field 가 그린다.
    return (<select style={inStyle} title="선택하면 금액·비고가 채워집니다" value={form.workTypeId||''} onChange={function(e){apply(workTypePatch(e.target.value, form))}}>
        <option value="">(선택 안 함)</option>
        {workTypes.filter(function(w){return w.active!==0}).map(function(w){return <option key={w.id} value={w.id}>{w.name}</option>})}
      </select>);
  };

  var settlementItemBody=function(f, issueTaxInvoice){
    return {agencyId:parseInt(stlAgencyId),date:f.date,carNo:f.carNo,carType:f.carType||'',vinLast6:f.vinLast6,
      registrationTax:parseInt(f.registrationTax)||0,stampDuty:parseInt(f.stampDuty)||0,serviceFee:parseInt(f.serviceFee)||0,
      // 재교부 그룹 3분할 + 과세 기타 (M1). 구 wonbuFee/reissueFee 는 입력이 없어 0 으로만 나간다(legacy 보존 컬럼).
      wonbuFee:parseInt(f.wonbuFee)||0,reissueFee:parseInt(f.reissueFee)||0,
      reissueStampDuty:parseInt(f.reissueStampDuty)||0,reissueServiceFee:parseInt(f.reissueServiceFee)||0,otherTaxableFee:parseInt(f.otherTaxableFee)||0,
      otherFee:parseInt(f.otherFee)||0,extraServiceFee:parseInt(f.extraServiceFee)||0,extraServiceNote:f.extraServiceNote||'',
      // A3-4 — 가입 거래처(회사 연결) 스코프는 clientId 가 0 이라 서버가 (미지정)으로 청구하고
      // 조회에서도 탈락시켰다. companyId 를 함께 실어 서버가 그 회사에 연결된 단위로 귀속시키게 한다.
      district:f.district||'',note:f.note,noteDetail:f.noteDetail||'',jobKind:jobKindOf(f.jobKind),month:stlMonth,issueTaxInvoice:issueTaxInvoice?1:0,
      clientId:stlClientId||0,companyId:stlCompanyId||0};
  };
  // W-4 — 계정(a:) 스코프에는 청구 좌표가 없다. 그대로 수기 추가하면 body 의 companyId/clientId 가
  //   0/0 이라 (미지정)으로 청구되고, 정작 그 계정 화면에서는 안 보인다 (A3-4 와 같은 증상).
  //   전체('')/회사/거래처 스코프는 종전대로 (전체 스코프의 (미지정) 청구는 의도된 동작).
  var addItemBlocked=!!stlAccountId;
  var openAddItem=function(){
    if(addItemBlocked){showToast('대행사 전체가 아닌 하위 업체를 선택하세요','err');return;}
    // B-2 — 기본 발행형은 지금 보는 거래처 설정을 따른다(비발행 거래처는 꺼진 채로 연다).
    setAddItemForm(Object.assign({},ADD_ITEM_DEFAULT,{issueTaxInvoice:!stlData||stlData.issueTaxInvoice!==0}));
    setShowAddItem(true);   // 그 날짜 맨 아래 추가 (서버 정렬 그대로)
  };
  var submitAddItem=function(){
    authFetch(API+'/settlement-items',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(settlementItemBody(addItemForm,addItemForm.issueTaxInvoice!==false))}).then(function(r){return r.json()}).then(function(d){
      if(!d.success){showToast(d.error||'실패','err');return;}
      showToast('추가 완료');setShowAddItem(false);setAddItemForm(ADD_ITEM_DEFAULT);reloadStl();
    }).catch(function(){showToast('서버 오류','err')});
  };
  // 장부 행 순서 변경 — 같은 날짜 그룹의 표시 순서 배열을 그대로 확정한다(서버가 sortOrder=index 저장).
  // 실패하면 재조회로 화면을 서버 진실로 되돌린다(낙관적 표시를 하지 않으므로 원복 = 재조회).
  var saveOrder=function(ids){
    if(reorderBusy||!ids||ids.length<2)return;
    setReorderBusy(true);
    authFetch(API+'/settlement-items/reorder',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({ids:ids})})
      .then(function(r){
        return r.json().catch(function(){return null}).then(function(d){return {ok:r.ok,status:r.status,data:d}});
      })
      .then(function(x){
        if(!x.ok||!x.data||!x.data.success){
          // 400 = sortOrder 컬럼 없음(마이그레이션 전 서버). 그 외는 일반 실패.
          showToast(x.status===400?'정렬은 서버 업데이트 후 가능':'순서 저장 실패','err');
        }
        setReorderBusy(false);reloadStl();
      })
      .catch(function(){showToast('순서 저장 실패','err');setReorderBusy(false);reloadStl()});
  };
  // ▲▼ — 같은 날짜 그룹 안에서 이웃과 자리 교환
  var moveItem=function(group,idx,delta){
    var to=idx+delta;
    if(to<0||to>=group.length)return;
    var ids=group.map(function(i){return i.id});
    var moved=ids[idx];ids[idx]=ids[to];ids[to]=moved;
    saveOrder(ids);
  };
  // 데스크톱 드래그 — 같은 날짜 그룹 안에서만 drop 허용(다른 그룹 위에서는 dropEffect none + 강조 없음)
  var dragEnd=function(){setDragId(0);setDropId(0);dragRef.current=null};
  var dropOnRow=function(group,gDate,it,e){
    var src=dragRef.current;
    dragEnd();
    if(!src||src.id===it.id||src.date!==gDate)return;
    e.preventDefault();
    var ids=group.map(function(i){return i.id});
    var from=ids.indexOf(src.id),to=ids.indexOf(it.id);
    if(from<0||to<0)return;
    ids.splice(to,0,ids.splice(from,1)[0]);
    saveOrder(ids);
  };
  // ▲▼ 버튼 — 구 [↓+] 와 같은 시각 문법(테두리·회색), 경계/전송중이면 흐리게
  var moveBtnSt=function(off){
    return {background:'none',border:'1px solid #e2e8f0',borderRadius:6,color:'#64748b',cursor:off?'default':'pointer',
      fontSize:11,lineHeight:1,padding:isMobile?'6px 8px':'3px 6px',whiteSpace:'nowrap',opacity:off?.35:1};
  };
  // #303 — 폼의 귀속 콤보가 진실. 기본은 조회 스코프 따라감, 거래처 미선택('')이면 (미지정) 입금.
  var effPayScope=payScope===null?stlScope:payScope;
  var addPayment=function(){
    var amount=parseInt(payForm.amount)||0;
    if(amount<=0){showToast('입금액을 입력하세요','err');return;}
    // 계정(브로커) 스코프 = 계정 레벨 총액 입금 (신 API). 단위/미지정은 호환 경로 (미지정 = companyId/clientId 없음).
    var p=scopeParams(effPayScope);
    var url=p.accountId?(API+'/billing-accounts/'+p.accountId+'/payments'):(API+'/settlement-payments');
    var body=p.accountId?{month:stlMonth,date:payForm.date,amount:amount,memo:payForm.memo||''}
      :{agencyId:parseInt(stlAgencyId),month:stlMonth,date:payForm.date,amount:amount,memo:payForm.memo||''};
    if(!p.accountId){if(p.companyId)body.companyId=p.companyId; else if(p.clientId)body.clientId=p.clientId;}
    authFetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json()}).then(function(d){
      if(d.success){showToast('입금 등록 완료');setPayForm({date:ymdLocal(new Date()),amount:'',memo:''});setPayScope(null);reloadStl()}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  // #304 — 이력 행 거래처 재지정. PATCH 는 companyId/clientId 좌표만 받는다 (a: 계정 취합은 대상 아님).
  // 성공 시 응답 id 재사용 금지(원장 경로는 재전기로 id 가 바뀜) — 반드시 재조회.
  var payRowScope=function(p){
    if((p.clientId||0)>0)return 'u:'+p.clientId;
    if((p.companyId||0)>0){
      var uid=unitByCompany[p.companyId];
      return uid?('u:'+uid):'';
    }
    return '';
  };
  // A3-2 — 귀속이 있는 행인데 색인에서 단위를 못 찾으면 콤보가 (미지정)으로 보인다. 그 상태로 저장하면
  // 귀속이 날아가므로 편집 진입 자체를 막는다 (색인은 loadAccounts 가 채운다 — 로드 실패/직후 진입 대비).
  var payRowResolvable=function(p){ return !((p.companyId||0)>0 && !payRowScope(p)); };
  // W-1 — unassign(명시적 (미지정) 복귀) 은 **원래 귀속이 있던 행** 에서만 의미가 있다.
  //   좌표가 없는 행(원래 미지정 · 대행업체 이름으로 받은 계정 레벨 통합 입금)에 unassign:1 을
  //   실어 보내면 서버가 전기를 (미지정) 계정으로 옮겨 원 계정 미수가 되살아난다.
  var payRowHadScope=function(p){ return !!((p.companyId||0)||(p.clientId||0)); };
  var startEditPay=function(p){
    if(!payRowResolvable(p)){showToast('거래처 정보를 불러오는 중입니다. 잠시 후 다시 시도하세요','err');return;}
    setEditPay({id:p.id,scope:payRowScope(p),date:p.date||'',amount:String(p.amount||''),memo:p.memo||'',
      hadScope:payRowHadScope(p)});   // 편집 진입 시점의 귀속 유무 — 저장 시 unassign 판단 근거
  };
  // 입금 행 거래처 재지정 — 이력 행(✎)과 미수 보드의 (미지정) 행이 공용으로 쓴다.
  // hadScope: 원래 귀속이 있던 행이면 true (보드의 (미지정) 행은 전부 false → unassign 안 보냄).
  var patchPayScope=function(id,sc,done,hadScope){
    var p2=scopeParams(sc);
    if(p2.accountId){showToast('대행사 전체가 아닌 하위 업체를 선택하세요','err');return;}
    // unassign — 0/0 은 서버가 '재지정 없음'으로 본다(A3-2). 원래 귀속이 있던 행에서 콤보의
    // (미지정)을 직접 고른 경우만 해제 (W-1).
    authFetch(API+'/settlement-payments/'+id,{method:'PATCH',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({companyId:p2.companyId||0,clientId:p2.clientId||0,
        unassign:(p2.companyId||p2.clientId)?0:(hadScope?1:0)})}).then(function(r){return r.json()}).then(function(d){
      if(d.success){showToast('거래처 변경 완료');if(done)done();reloadStl()}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  // 행 전체 편집 저장 — 스코프 + 입금일/금액/메모를 한 번의 PATCH 로. 응답 id 재사용 금지(원장은 재전기) → reloadStl 재조회.
  var saveEditPay=function(){
    if(!editPay)return;
    var amount=parseInt(editPay.amount,10)||0;
    // 0원 입금 행은 원장에 무의미 — 되돌리려는 의도(잘못 지정한 입금)에 다음 행동을 같이 알려준다.
    if(amount<=0){showToast('금액은 1원 이상이어야 합니다 — 입금을 취소하려면 [삭제] 버튼, 거래처가 잘못됐으면 거래처 칸을 바꾸세요','err');return;}
    var p2=scopeParams(editPay.scope);
    if(p2.accountId){showToast('대행사 전체가 아닌 하위 업체를 선택하세요','err');return;}
    authFetch(API+'/settlement-payments/'+editPay.id,{method:'PATCH',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({companyId:p2.companyId||0,clientId:p2.clientId||0,
        // 0/0 은 '재지정 없음' — 명시 해제만 플래그 (A3-2). 원래 귀속이 없던 행은 해제할 것도 없다 (W-1).
        unassign:(p2.companyId||p2.clientId)?0:(editPay.hadScope?1:0),
        date:editPay.date||'',amount:amount,memo:editPay.memo||''})}).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
      var d=res.data||{};
      if(res.status===409&&d.code==='VERSION_CONFLICT'){
        showToast((d.error||'다른 사용자가 이미 수정했습니다')+' — 최신 내용으로 다시 불러왔습니다','err');
        setEditPay(null);reloadStl();
        return;
      }
      if(d.success){showToast('수정 완료');setEditPay(null);reloadStl()}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  var deletePayment=function(p){
    if(!window.confirm(p.date+' '+fmt(p.amount)+'원 입금 기록을 삭제할까요?'))return;
    authFetch(API+'/settlement-payments/'+p.id,{method:'DELETE'}).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
      var d=res.data||{};
      if(res.status===409&&d.code==='VERSION_CONFLICT'){
        showToast((d.error||'다른 사용자가 이미 수정했습니다')+' — 최신 내용으로 다시 불러왔습니다','err');
        setEditPay(null);reloadStl();
        return;
      }
      if(d.success){showToast('삭제 완료');reloadStl()}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  var startEditItem=function(it){
    setLinkedExtraTaxable(0); // B-1 — 새 항목을 열 때마다 리셋(다음 rows 로드가 다시 채운다)
    setEditItem({id:it.id,version:it.version,carId:it.carId||null,date:it.date||'',carNo:it.carNo||'',carType:it.carType||'',vin:it.vin||'',vinLast6:it.vinLast6||'',
      registrationTax:String(it.registrationTax||0),stampDuty:String(it.stampDuty||0),wonbuFee:String(it.wonbuFee||0),serviceFee:String(it.serviceFee||0),
      baseFee:String(it.baseFee||23000),reissueFee:String(it.reissueFee||0),otherFee:String(it.otherFee||0),extraServiceFee:String(it.extraServiceFee||0),
      // 재교부 그룹 3분할 + 과세 기타 (M1). 증지 칸은 이미 값이 있으면 사용자가 정한 것으로 본다.
      reissueStampDuty:String(it.reissueStampDuty||0),reissueServiceFee:String(it.reissueServiceFee||0),otherTaxableFee:String(it.otherTaxableFee||0),
      rsTouched:(it.reissueStampDuty||0)>0?1:0,
      // 발행형 판정에 재교부세액도 센다 — 수수료 세액이 0 이고 재교부세액만 있는 행에서
      // 이 체크가 꺼진 채 저장되면 서버가 비발행으로 재계산해 재교부세액이 통째로 사라진다 (H3).
      extraServiceNote:it.extraServiceNote||'',district:it.district||'',note:it.note||'',noteDetail:it.noteDetail||'',jobKind:jobKindOf(it.jobKind),
      // 요청 연결 항목(W8) — 구 서버는 이 필드가 없어(undefined) 0 으로 폴백, 미연결 항목과 같은 동작.
      requestId:it.requestId||0,
      issueTaxInvoice:(it.vatAmount||0)>0||(it.reissueVat||0)>0});
  };
  // 요청 부가서비스 편집기 저장 직후 — 서버 파생값(Σfee 재교부 제외 · 이름 ' / ' 연결)을 그대로 미러링해
  // 드로어 표시를 즉시 갱신하고, 장부 목록/소계는 reloadStl 로 재조회한다 (§4 서버 계약과 같은 식).
  var onServicesSaved=function(rows){
    var list=Array.isArray(rows)?rows:[];
    var billable=list.filter(function(r){return r.kind!=='reissue'});
    var sum=billable.reduce(function(s,r){return s+(parseInt(r.fee,10)||0)},0);
    var note=billable.map(function(r){return r.name}).filter(function(n){return !!n}).join(' / ');
    setEditItem(function(prev){return prev?Object.assign({},prev,{extraServiceFee:String(sum),extraServiceNote:note}):prev});
    reloadStl();
  };
  // B-1 — 편집기 rows 가 로드/편집될 때마다(저장 전) 통지. 부가서비스 금액 표시를 rows 합으로 live 반영하고
  // 세액 미리보기 과세표준(계산서 포함분, 재교부 제외)도 여기서 갱신한다.
  var onLinkedRowsChange=function(rows){
    var list=Array.isArray(rows)?rows:[];
    var billable=list.filter(function(r){return r.kind!=='reissue'});
    var sum=billable.reduce(function(s,r){return s+(parseInt(r.fee,10)||0)},0);
    var taxable=billable.filter(function(r){return r.issueTaxInvoice!==0}).reduce(function(s,r){return s+(parseInt(r.fee,10)||0)},0);
    setLinkedExtraTaxable(taxable);
    setEditItem(function(prev){return prev?Object.assign({},prev,{extraServiceFee:String(sum)}):prev});
  };
  var saveEditItem=function(){
    // A3-1 — baseFee 를 반드시 함께 보낸다. legacy lump 행(분리 4항목 0 · baseFee 에 실비 총액)에서
    // 이 값이 빠지면 서버가 기본단가로 덮어쓸 수 있다. 서버도 기존 행 기준 병합으로 이중 방어.
    var body={date:editItem.date,carNo:editItem.carNo,vinLast6:editItem.vinLast6,baseFee:parseInt(editItem.baseFee)||0,
      registrationTax:parseInt(editItem.registrationTax)||0,stampDuty:parseInt(editItem.stampDuty)||0,serviceFee:parseInt(editItem.serviceFee)||0,
      // 구 원부·구 재교부는 입력란이 없다 — 읽은 값을 그대로 되돌려 보내 legacy 금액을 보존한다
      // (미전송이면 서버가 기존값을 승계하지만, 명시 전송이 A3-1 의 lump 보존 판정과 함께 안전하다).
      wonbuFee:parseInt(editItem.wonbuFee)||0,reissueFee:parseInt(editItem.reissueFee)||0,
      reissueStampDuty:parseInt(editItem.reissueStampDuty)||0,reissueServiceFee:parseInt(editItem.reissueServiceFee)||0,
      otherTaxableFee:parseInt(editItem.otherTaxableFee)||0,otherFee:parseInt(editItem.otherFee)||0,
      extraServiceFee:parseInt(editItem.extraServiceFee)||0,extraServiceNote:editItem.extraServiceNote||'',
      district:editItem.district||'',note:editItem.note,noteDetail:editItem.noteDetail};
    // 요청 기원 행(carId)은 서버가 업무 변경을 400 으로 거부 — 수기 행만 싣는다.
    if(!editItem.carId) body.jobKind=editItem.jobKind;
    // B-3 — 체크박스를 실제로 건드렸을 때만 명시 전송. 비고만 고친 저장은 서버의 줄별 issueTaxInvoice(null=
    // 월 토글 대상)를 건드리지 않는다 (explicitIssue — utils/cancelSettlement.js:358).
    if(editItem.issueTouched) body.issueTaxInvoice=editItem.issueTaxInvoice?1:0;
    // ERP 차량 연결 행의 차종은 cars JOIN 표시값 — 되돌려 보내면 수기 컬럼을 오염시키므로 walk-in 만 전송.
    if(!editItem.carId) body.carType=editItem.carType||'';
    // #cap-lock 계약: 낙관적 락(version) — 있을 때만 전송(구 서버/undefined 방어).
    if(editItem.version!==undefined&&editItem.version!==null)body.version=editItem.version;
    authFetch(API+'/settlement-items/'+editItem.id,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
      var d=res.data||{};
      if(res.status===409&&d.code==='VERSION_CONFLICT'){
        showToast((d.error||'다른 사용자가 이미 수정했습니다')+' — 최신 내용으로 다시 불러왔습니다','err');
        setEditItem(null);reloadStl();
        return;
      }
      if(d.success){showToast('수정 완료');setEditItem(null);reloadStl()}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };
  // 삭제는 항목 상세(수정 모달)에서만 — 리스트 행의 x 버튼은 오조작 위험이 커 제거(2026-08 Jin).
  var deleteEditItem=function(){
    if(!editItem) return;
    if(!window.confirm('이 정산 항목을 삭제할까요?\n'+(editItem.carNo||'')+' / '+(editItem.date||''))) return;
    var delBody={};
    if(editItem.version!==undefined&&editItem.version!==null)delBody.version=editItem.version;
    authFetch(API+'/settlement-items/'+editItem.id,{method:'DELETE',headers:{'Content-Type':'application/json'},body:JSON.stringify(delBody)}).then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
      var d=res.data||{};
      if(res.status===409&&d.code==='VERSION_CONFLICT'){
        showToast((d.error||'다른 사용자가 이미 수정했습니다')+' — 최신 내용으로 다시 불러왔습니다','err');
        setEditItem(null);reloadStl();
        return;
      }
      if(d.success){showToast('삭제 완료');setEditItem(null);reloadStl()}else showToast(d.error||'실패','err');
    }).catch(function(){showToast('서버 오류','err')});
  };

  // ── 정산서 발행 (R3 — 불변 스냅샷) ────────────────────────────────────────
  // 발행 단위는 청구 계정. 성공 시 공유 링크를 클립보드로 넣고 보관함 진입 액션을 띄운다.
  var copyText=function(t,cb){
    if(!t){cb(false);return;}
    try{
      if(navigator.clipboard&&navigator.clipboard.writeText){
        navigator.clipboard.writeText(t).then(function(){cb(true)}).catch(function(){cb(false)});
        return;
      }
    }catch(e){}
    cb(false);
  };
  // 계정 인자를 받는 형태 — 미수 보드의 [발행 →] 는 스코프 이동 직후(state 반영 전) 호출하므로
  // issueAcc state 대신 계정 객체를 넘긴다.
  var issueStatementFor=function(acc){
    if(!acc||issuing)return;
    if(!window.confirm(stlMonth+' '+acc.name+' 정산서를 발행할까요?'))return;
    setIssuing(true);
    authFetch(API+'/billing-accounts/'+acc.id+'/statements',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({period:stlMonth})})
      .then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
        setIssuing(false);
        var d=res.data||{};
        if(res.status===409&&d.code==='STATEMENT_CONCURRENT'){
          showToast(d.error||'다른 사용자가 동시에 발행했습니다','err');
          reloadStl();
          return;
        }
        if(!d||!d.success){showToast((d&&d.error)||'발행 실패','err');return;}
        setIssued({id:d.id||0,shareUrl:d.shareUrl||'',name:acc.name,accountId:acc.id,closingBalance:d.closingBalance||0});
        copyText(d.shareUrl||'',function(ok){showToast(ok?'정산서 발행 완료 · 공유 링크를 복사했습니다':'정산서 발행 완료')});
        setBoardKey(function(k){return k+1});
      }).catch(function(){setIssuing(false);showToast('발행 실패','err')});
  };
  var issueStatement=function(){issueStatementFor(issueAcc)};
  // 보드 행의 다음 액션 — 스코프로 이동한 뒤 그 계정으로 발행 확인 흐름 진입.
  var boardIssue=function(sc){
    pickScope(sc);
    var aid=scopeAccountId(sc), uid=scopeUnitId(sc);
    var acc=aid?accountById[aid]:(uid&&unitIdx[uid]?accountById[unitIdx[uid].accountId]:null);
    if(!acc){showToast('정산서를 발행할 계정을 찾지 못했습니다','err');return;}
    issueStatementFor(acc);
  };
  // 보드 행의 [입금 →] — 대상 이동 + 화면을 입금으로 (두 축을 한 번에 바꾸는 유일한 지점)
  var boardPay=function(sc){pickScope(sc);setStlView('payments')};

  // ── walk-in 말소증 등록 (#294 웹 확장) ─────────────────────────────────────
  // 사무실 스캔본(이미지/PDF)을 올리면 서버가 OCR → 정산행 생성 + 말소폴더 저장까지 한다.
  // 서버가 clientId 필수(요율 resolveFeesByClient) → walk-in 거래처 스코프에서만 가능.
  var openMalsoPicker=function(){
    if(!stlClientId){showToast('말소증 등록은 거래처(비가입 업체)를 먼저 선택하세요','err');return;}
    if(malsoBusy)return;
    if(malsoInputRef.current)malsoInputRef.current.click();
  };
  // 응답 → 결과 행. 서버는 OCR 실패여도 행을 만들고 warnings 로 알린다(❌ 는 요청 자체 실패만).
  var malsoResultOf=function(file, d){
    var base={name:file.name, carNo:(d&&d.ocr&&d.ocr.carNo)||'', itemId:(d&&d.itemId)||0, warnings:(d&&d.warnings)||[]};
    if(!d||!d.success)return Object.assign(base,{status:'fail',error:(d&&d.error)||'업로드 실패'});
    if(d.duplicated)return Object.assign(base,{status:'dup'});
    if(base.warnings.length>0)return Object.assign(base,{status:'warn'});
    return Object.assign(base,{status:'ok'});
  };
  // 순차 업로드 — 동시 전송 금지(서버 OCR 부하 + 중복 판정 경합). 1건 실패해도 다음 파일 계속.
  var uploadMalsoFiles=function(files){
    setMalsoBusy({done:0,total:files.length});
    var results=[];
    var step=function(i){
      if(i>=files.length){setMalsoBusy(null);setMalsoResults(results);return;}
      var file=files[i];
      var fd=new FormData();
      fd.append('file',file);
      // Content-Type 수동 지정 금지 — authFetch 가 FormData 면 건너뛰고 브라우저가 boundary 를 붙인다.
      authFetch(API+'/agency-clients/'+stlClientId+'/walkin-malso',{method:'POST',body:fd})
        .then(function(r){return r.json().catch(function(){return {success:false,error:'응답 오류 ('+r.status+')'}})})
        .then(function(d){results.push(malsoResultOf(file,d))})
        .catch(function(){results.push({name:file.name,carNo:'',itemId:0,warnings:[],status:'fail',error:'업로드 실패 (네트워크)'})})
        .then(function(){setMalsoBusy({done:i+1,total:files.length});step(i+1)});
    };
    step(0);
  };
  var onMalsoFilesPicked=function(e){
    var files=Array.prototype.slice.call(e.target.files||[]);
    e.target.value=''; // 같은 파일 재선택 허용
    if(files.length>0)uploadMalsoFiles(files);
  };
  var closeMalsoResults=function(){setMalsoResults(null);reloadStl()};
  // ⚠️ 행 → 해당 정산 항목 수정 모달. 월 귀속은 서버가 말소일 기준으로 정해 현재 조회 월과
  // 다를 수 있으므로, 재조회 결과에서 찾아 열고 없으면 안내한다.
  var openMalsoResultItem=function(r){
    if(!r.itemId)return;
    setMalsoResults(null);
    var qs=qsOf(scopeParams(stlScope));
    authFetch(API+'/settlement-items/'+stlAgencyId+'/'+stlMonth+qs).then(function(res){return res.json()}).then(function(d){
      setStlData(d);
      var found=((d&&d.items)||[]).filter(function(it){return it.id===r.itemId})[0];
      if(found)startEditItem(found);
      else showToast('해당 항목이 이번 달 목록에 없습니다 (말소일 기준 다른 월일 수 있음)','err');
    }).catch(function(){showToast('조회 실패','err')});
  };

  // 말소증 유무 배지 — 장부 표 [말소증] 버튼 활성/비활성 판정. 배치 조회(200개 청크, DoneList 관례).
  // hook 은 위 `if(!active) return null` 보다 먼저 와야 한다 — 매 렌더 동일 순서로 호출돼야 하므로.
  var [certFlags, setCertFlags] = useState(null);
  var [viewCertItem, setViewCertItem] = useState(null); // 뷰어에 띄운 정산 항목 {id, carNo}
  var flagIds=((stlData&&stlData.items)||[]).map(function(i){return i.id}).sort(function(a,b){return a-b}).join(',');
  useEffect(function(){
    var ids=flagIds?flagIds.split(','):[];
    if(!ids.length){setCertFlags(null);return undefined}
    var alive=true;
    var chunks=[];
    for(var i=0;i<ids.length;i+=200)chunks.push(ids.slice(i,i+200));
    Promise.all(chunks.map(function(c){
      return authFetch(API+'/settlement-items/malso-flags?ids='+c.join(','))
        .then(function(res){return res.ok?res.json():null})
        .catch(function(){return null});
    })).then(function(list){
      if(!alive)return;
      var merged={};
      list.forEach(function(d){if(d)Object.keys(d).forEach(function(k){merged[k]=d[k]===true})});
      setCertFlags(merged);
    });
    return function(){alive=false};
  },[flagIds]);
  // 장부 행 [말소증] 버튼 — 플래그 true 만 활성. 행 클릭(수정 모달)과 겹치지 않게 stopPropagation.
  var malsoDocBtn=function(it){
    var on=certFlags?certFlags[it.id]===true:false;
    return(<button type="button" className="btn btn-outline btn-sm" disabled={!on}
      title={!certFlags?'확인 중':(on?'말소증 원본 보기':'말소증 없음')}
      onClick={function(e){e.stopPropagation();if(on)setViewCertItem({id:it.id,carNo:it.carNo})}}
      style={on?{fontSize:11,background:'#ecfdf5',borderColor:'#6ee7b7',color:'#047857',fontWeight:700}:{fontSize:11,color:'#cbd5e1'}}>말소증</button>);
  };

  if(!active) return null;
  var items=(stlData&&stlData.items)||[];
  var districts=items.map(function(i){return(i.district||'').trim()}).filter(function(d,ix,arr){return d&&arr.indexOf(d)===ix});
  // 세액 컬럼 노출 = 발행형(issueTaxInvoice!==0)이거나, 이미 적재된 세액이 있을 때. 전체(스코프 미선택) 시 백엔드가 1 반환.
  // 거래처가 나중에 비발행으로 전환되면 issueTaxInvoice=0 이 되지만 과거 행의 vatAmount 는 소계에 그대로 남아 있다 —
  // 그때 컬럼을 숨기면 화면 컬럼 합 ≠ 소계 가 된다(실측: 등록세+증지+수수료 31,200 vs 소계 32,590).
  // 세액이 두 칸(수수료 세액 + 재교부세액)이 됐으므로 판정도 둘의 합으로 본다 —
  // 수수료 세액만 보면 재교부세액만 있는 달에서 컬럼이 사라져 화면 컬럼 합 ≠ 소계 가 된다.
  var totalReissueVat=sumBy(items,'reissueVat');
  var showVat=!!stlData&&(stlData.issueTaxInvoice!==0||((stlData.totalVat||0)+totalReissueVat)>0);
  // 거래처 컬럼 — 단위 스코프가 아닐 때(전체 / 계정 취합) 노출. 계정 취합 장부가 어느 하위 업체 건인지 구분.
  var showCompanyCol=isCancelAgency&&!stlUnitId;
  // 엑셀 장부 열 순서 — 기타 계열이 재교부 그룹 **앞**. (기타(과세)·부가서비스는 엑셀에 없는 우리 칸이라 기타 옆.)
  var rightCols=['등록세','증지','수수료','세액','기타','기타(과세)','부가서비스','재교부증지','재교부수수료','재교부세액','소계'];
  var headers=['No','월일'].concat(showCompanyCol?['거래처']:[]).concat(['차종','차량번호','차대번호(뒷8)','등록세','증지','수수료'])
    .concat(showVat?['세액']:[]).concat(['기타','기타(과세)','부가서비스','재교부증지','재교부수수료','재교부세액','소계','비고','말소증','구청']);
  var subtotalIdx=headers.indexOf('소계');
  var openingBalance=(stlData&&stlData.openingBalance)||0;
  var totalPaid=(stlData&&stlData.totalPaid)||0;
  var grandTotal=(stlData&&stlData.grandTotal)||0;
  // 수기 조정 합(미취소) — 원장상 그 달 청구(adjustment)라 '그 달 청구'·'미수 (그 달 말)'에 더한다. 장부 표 합계(grandTotal)는 항목만.
  var adjTotal=adjustmentTotal(adjustments);
  // 엑셀 이관 조정 합(impAdjTotal) — 이관이 엑셀 미수에 맞추려 넣은 조정(ref_type excel_import). 수기 목록엔 없지만 원장상 그 달 청구.
  // 빼면 '미수 (그 달 말)' ≠ 다음 달 전월미수 · 거래처 행 합 (YK 7월 350,500 차이, Jin 10/22).
  // 미수 (그 달 말) = 전월미수 + 그 달 청구 − 그 달 입금 — 엑셀 청구서의 미수합계 (Jin 10/20).
  // YK 처럼 그 달 청구서에 그 달 청구분 입금(익월 초 포함)을 적는 거래처도 0 으로 맞는다 (구 '미수 (지난달까지)' = opening − 입금 은 음수로 보였다).
  var monthEndDue=openingBalance+grandTotal+adjTotal+impAdjTotal-totalPaid;
  var stlMonthNo=parseInt((stlMonth||'').slice(5),10)||0;
  var payments=(stlData&&stlData.payments)||[];
  var totalUnclassified=items.reduce(function(s,i){return s+unclassifiedCost(i)},0);
  // 숫자를 탭하면 이유(원장 전기)가 나온다 — 계정이 정해진 스코프에서만 열린다(전체 스코프는 비활성).
  // seg: opening(지난달 청구·이월) / paid(그 달 입금) / outstanding(미수 구성) / charged(그 달 청구) — 드로어가 해당 전기만 필터
  var openLedger=function(label,seg){
    if(!ledgerAccId)return;
    setDrawer({accountId:ledgerAccId,unitId:stlUnitId||0,seg:seg||'',month:stlMonth,label:(scopeAcc?scopeAcc.name+' · ':'')+label});
  };
  // 요약 스트립 세그먼트 — 구 흐름 4줄의 가로 판. 숫자 클릭 = 원장 거래 내역(드로어) 진입 유지.
  var flowSeg=function(label,value,opts){
    opts=opts||{};
    var num=fmt(value)+'원';
    // 모바일 2×2 — flexBasis 50% + 1행 하단 구분선 / 짝수칸 우측 구분선 (데스크탑은 4칸 가로 1행 유지)
    var i=opts.i||0;
    var segSt=isMobile
      ?{flexBasis:'50%',maxWidth:'50%',minWidth:0,boxSizing:'border-box',padding:'9px 12px',
        // i<2 — 4칸(2×2) 중 1행 2칸에만 아래 구분선.
        borderRight:(i%2===0)?'1px solid #f1f5f9':'none',borderBottom:(i<2)?'1px solid #f1f5f9':'none',
        background:opts.hero?'#eff6ff':'transparent'}
      :{flex:1,minWidth:140,padding:'9px 14px',borderRight:'1px solid #f1f5f9',background:opts.hero?'#eff6ff':'transparent'};
    return(
    <div key={label} style={Object.assign({position:'relative'},segSt)}>
      <div style={{fontSize:10,color:'#94a3b8',fontWeight:600,paddingRight:opts.help?18:undefined}}>{label}</div>
      <div style={{fontSize:15,fontWeight:800,marginTop:1,color:opts.color||'#1e293b',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        {ledgerAccId
          ? <button onClick={function(){openLedger(label,opts.seg)}} title="거래 내역 보기" style={{background:'none',border:'none',padding:0,font:'inherit',color:'inherit',cursor:'pointer',borderBottom:'1px dotted #94a3b8'}}>{num}</button>
          : <span>{num}</span>}
        {opts.extra}
      </div>
      {!!opts.caption&&<div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>{opts.caption}</div>}
      {/* ⓘ 설명 — 칸 우상단. 숫자 클릭(원장 드로어)과 분리 (Jin 10/20) */}
      {!!opts.help&&<SettlementStripHelp k={opts.help} title={label} isMobile={isMobile}
        open={stripHelp===opts.help} onOpenChange={function(v){setStripHelp(v?opts.help:'')}}
        n={{month:stlMonthNo,opening:openingBalance,paid:totalPaid,grandTotal:grandTotal,adj:adjTotal,imp:impAdjTotal}}/>}
    </div>);};
  // ── 대상 × 화면 매트릭스 (IA v2) ──────────────────────────────────────────
  var vOverview=stlView==='overview', vPay=stlView==='payments', vStmt=stlView==='statements';
  // 전체 × 현황 = 미수 보드. admin/doc_agency 갈래는 계정 색인이 없어 보드 대신 기존 장부를 그대로 쓴다.
  var showBoard=!!isCancelAgency&&!stlScope&&vOverview;
  // 계정 스코프 × 현황 = 계정 카드(대행사면 하위 업체 표). 장부 뷰에서는 취합 장부가 본문이라 접는다.
  // 차량 장부 표 — '장부' 뷰. admin/doc_agency 는 화면 축(세그먼트)이 없어 현황 = 장부.
  var showLedgerTable=stlView==='ledger'||(!isCancelAgency&&vOverview);
  // 브로커 하위 단위를 보고 있으면 상위 대행사로 한 단계 복귀 (구 브레드크럼 대체 — 진입점 1개)
  var parentAcc=(curUnit&&scopeAcc&&scopeAcc.kind==='broker')?scopeAcc:null;
  // 툴바 제목 (admin/doc_agency 갈래 전용) — cancel_agency 는 헤더의 대상 칩이 대신한다.
  var scopeTitle=curUnit?curUnit.name:(scopeAcc?scopeAcc.name:(stlScope?'':'전체 거래처'));
  // admin/doc_agency 업체 검색 — 필터를 한 번만 계산해 목록·빈상태·키보드 선택이 같은 배열을 본다.
  var agencyHits=agencySearch
    ?agencies.filter(function(a){return a.name.toLowerCase().indexOf(agencySearch.toLowerCase())!==-1||(a.ceo||'').indexOf(agencySearch)!==-1||(a.bizNo||'').indexOf(agencySearch)!==-1})
    :[];
  var pickAgency=function(a){setStlAgencyId(String(a.id));setAgencySearch(a.name);setAgencyHi(-1);setStlScope('');loadStl(a.id,stlMonth,'')};
  // 세금계산서 발행(기록) — ⋯ 메뉴 진입. 발행형 가입 거래처에서만 노출된다.
  var submitTaxInvoice=function(){
    var c=(taxInv&&taxInv.computed)||{};
    var supply=(c.supplyAmount||0), tax=(c.taxAmount||0), total=(c.totalAmount||0);
    // taxInv 가 로드된 경우에만 client 0-가드 적용 (미로드/실패 시 서버 응답이 권위) — guard-symmetry
    if(taxInv&&taxInv.computed&&total<=0){showToast('과세 공급가액 0 — 발행 대상 없음','err');return;}
    if(!window.confirm('세금계산서를 발행(기록)하시겠습니까?\n공급가액 '+supply.toLocaleString()+'원 / 세액 '+tax.toLocaleString()+'원 / 합계 '+total.toLocaleString()+'원'))return;
    authFetch(API+'/settlement-tax-invoice',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({agencyId:parseInt(stlAgencyId),companyId:stlCompanyId,month:stlMonth})}).then(function(r){return r.json()}).then(function(d){
      if(d&&d.ok){showToast('세금계산서 '+(d.status==='issued'?'발행 완료':d.status==='test'?'테스트 발행':'양식 기록')+' (합계 '+(d.totalAmount||0).toLocaleString()+'원)');}
      else{showToast((d&&d.error)||'발행 실패','err');}
      loadTaxInv(stlAgencyId,stlMonth,stlCompanyId);
    }).catch(function(){showToast('발행 실패','err')});
  };
  // 세금계산서 발행 기록 취소 — 실발행(status==='issued')이면 서버가 409 로 막는다(취소 불가 메시지 그대로 노출).
  var cancelTaxInvoice=function(){
    if(!taxInv||!taxInv.record)return;
    if(!window.confirm(stlMonth+' 세금계산서 발행 기록을 취소할까요?'))return;
    var qs=stlUnitId?('?unitId='+stlUnitId):'';
    authFetch(API+'/settlement-tax-invoice/'+stlAgencyId+'/'+stlCompanyId+'/'+stlMonth+qs,{method:'DELETE'})
      .then(function(r){return r.json().then(function(d){return {status:r.status,data:d}})}).then(function(res){
        var d=res.data||{};
        if(res.status===409){showToast(d.error||'실제 발행돼 취소할 수 없습니다','err');return;}
        if(!d.success){showToast(d.error||'취소 실패','err');return;}
        showToast('발행 기록을 취소했습니다');
        loadTaxInv(stlAgencyId,stlMonth,stlCompanyId);
      }).catch(function(){showToast('서버 오류','err')});
  };
  // ⋯ 메뉴 항목 — 클릭 시 메뉴를 닫고 기존 핸들러를 그대로 호출한다 (기능 이동만, 삭제 없음).
  var menuItemSt={display:'block',width:'100%',textAlign:'left',background:'none',border:'none',padding:'8px 12px',fontSize:13,color:'#334155',cursor:'pointer',whiteSpace:'nowrap'};
  // opts.disabled = 조건 미충족 항목 (감추지 않고 비활성 + 사유 서브라벨 — 왜 못 쓰는지가 보이게)
  var menuItem=function(label,fn,title,opts){
    opts=opts||{};
    var dis=!!opts.disabled;
    return (<button key={label} title={title||''} disabled={dis}
      style={dis?Object.assign({},menuItemSt,{opacity:0.45,cursor:'default'}):menuItemSt}
      onClick={function(){if(dis)return;setMenuOpen(false);fn()}}>
      {label}
      {!!(dis&&opts.sub)&&<div style={{fontSize:11,fontWeight:400,color:'#94a3b8',marginTop:1}}>{opts.sub}</div>}
    </button>);
  };
  // 툴바 2행의 액션 버튼 — 모바일 터치 타깃 40px (같은 툴바 1행의 ‹월› 위젯과 같은 높이). btn-sm 은 28~32px.
  var actBtnSt=isMobile?{minHeight:40}:undefined;
  // 모바일은 터치 타깃 확보(40px) — 데스크탑은 기존 24px 그대로
  var navBtnSt=isMobile
    ?{background:'none',border:'1px solid #e2e8f0',borderRadius:8,width:40,height:40,fontSize:15,color:'#475569',cursor:'pointer',padding:0,lineHeight:1,flexShrink:0}
    :{background:'none',border:'1px solid #e2e8f0',borderRadius:6,width:24,height:24,fontSize:13,color:'#475569',cursor:'pointer',padding:0,lineHeight:1};

  // 월 위젯 — 타이틀 배너 좌측(cancel_agency 포털)과 admin 툴바 양쪽에서 재사용.
  var monthWidget=(<span style={{display:'inline-flex',alignItems:'center',gap:4,flexShrink:0}}>
    <button title="전월" style={navBtnSt} onClick={function(){shiftMonth(-1)}}>‹</button>
    {showMonthInput
      ? <input type="month" autoFocus value={stlMonth} onBlur={function(){setShowMonthInput(false)}}
          onChange={function(e){setStlMonth(e.target.value);setStlData(null)}}
          style={{padding:'5px 8px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:13}}/>
      : <button title="월 직접 선택" onClick={function(){setShowMonthInput(true)}}
          style={isMobile
            ?{background:'none',border:'none',fontSize:13,fontWeight:700,color:'#334155',cursor:'pointer',padding:'0 4px',height:40,whiteSpace:'nowrap'}
            :{background:'none',border:'none',fontSize:13,fontWeight:700,color:'#334155',cursor:'pointer',padding:'2px 4px'}}>{monthLabel()}</button>}
    <button title="익월" style={navBtnSt} onClick={function(){shiftMonth(1)}}>›</button>
  </span>);

  // 대상 = 검색바(콤보: 입력 즉시 결과·엔터 선택, 닫힘 상태 라벨 = 현재 대상) + 전체 목록 드로어 버튼 (Jin 8/12)
  var scopeChip=(<span style={isMobile
    ?{display:'flex',alignItems:'center',gap:6,flex:1,minWidth:0}
    :{display:'inline-flex',alignItems:'center',gap:6,flexShrink:0}}>
    {/* 모바일 — 검색바는 툴바 1행에서 [목록 ▾][‹월›] 을 뺀 나머지 전부. 구 고정 130px 은 큰 화면(414/430px)
        에서도 그대로라 현재 대상 이름이 잘렸다 → flex 로 바꿔 폭에 비례해 늘어난다 (390px 실측 ≈132px). */}
    <span style={isMobile?{flex:1,minWidth:0}:{minWidth:230,maxWidth:280}}>
      <ClientScopeCombo searchOnly value={stlScope} data={scopeData} onChange={pickScope} placeholder="거래처 검색..."/>
    </span>
    <button title="거래처 전체 목록" onClick={function(){setPickerOpen(true)}}
      style={isMobile
        ?{border:'1px solid #2563EB',background:'#eff6ff',color:'#2563EB',borderRadius:8,padding:'0 10px',height:40,fontSize:12.5,fontWeight:700,cursor:'pointer',lineHeight:1,whiteSpace:'nowrap',flexShrink:0}
        :{border:'1px solid #2563EB',background:'#eff6ff',color:'#2563EB',borderRadius:8,padding:'6px 10px',fontSize:12.5,fontWeight:700,cursor:'pointer',lineHeight:1.4}}>목록 ▾</button>
  </span>);
  // ── 화면 세그먼트 — view 만 바꾼다 (대상 불변). ──
  // '현황'은 전체(보드)·대행사(하위 업체 표)에서만 의미 — 개별 거래처는 숨김 (Jin 8/12)
  var overviewAvailable=!stlScope||(!!stlAccountId&&!!scopeAcc&&scopeAcc.kind==='broker');
  // 일계표는 세그먼트가 아니라 '장부' 화면 안의 탭 (Jin 9/2 — 세그 축은 4개로 되돌린다).
  var viewSegs=[['overview','현황'],['ledger','장부'],['payments','입금'],['statements','정산서']]
    .filter(function(sg){return sg[0]!=='overview'||overviewAvailable});
  // 장부 화면 안 탭 — 첫 탭이 기존 월별 정산표, 나머지는 같은 거래처 스코프의 일계표 그룹 축.
  // (Jin 9/2 4차 — 구 [월별](연 12행) 탭은 제거. 집계는 조회 월 안에서만.)
  var vjTabs=[['ledger','월별 정산표'],['day','일별'],['week','주별']];
  // 일계표에 넘길 거래처 좌표 — 장부/정산서와 같은 scopeParams·qsOf 를 그대로 쓴다.
  var jScopeQs=qsOf(scopeParams(stlScope));
  // 밑줄 탭(시안 1) — 말소대행 하위 탭 공용 문법. 대행요청·거래처 세그와 같은 것을 쓴다 (Jin 8/26).
  var viewSeg=(<CaHeaderTabs isMobile={isMobile} items={viewSegs.map(function(s){
    return {label:s[1],on:stlView===s[0],onClick:function(){setStlView(s[0])}};
  })}/>);

  // 요약 4칸 스트립 — 엑셀 청구서 꼬리 순서: 전월미수 → 그 달 청구 → 그 달 입금 → 미수 (그 달 말) (Jin 10/20).
  // 스코프 화면과 전체 보드(현황)가 같은 것을 쓴다 (Jin 8/12).
  var flowStrip=(<div style={{display:'flex',alignItems:'stretch',flexWrap:'wrap',background:'#fff',border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden',marginBottom:12}}>
    {flowSeg('전월미수',openingBalance,{seg:'opening',i:0,help:'opening'})}
    {flowSeg(isCurMonth?'이번 달 청구 · 예상':stlMonthNo+'월 청구',grandTotal+adjTotal+impAdjTotal,{seg:'charged',i:1,help:'charged'})}
    {flowSeg((isCurMonth?'이번 달':stlMonthNo+'월')+' 입금',totalPaid,{color:'#059669',seg:'paid',i:2,help:'paid'})}
    {flowSeg('미수 ('+stlMonthNo+'월 말)',Math.abs(monthEndDue),{hero:true,color:monthEndDue>0?'#dc2626':'#059669',seg:'outstanding',i:3,help:'outstanding',caption:monthEndDue<0?'선수금 (초과 수금)':undefined,
      extra:monthEndDue===0?<span style={{fontSize:11,fontWeight:700,color:'#059669'}}>완납</span>:null})}
  </div>);

  return (<div style={{paddingBottom:80}}>{/* 하단 여백 — 표 마지막 행이 화면 끝에 붙지 않게 (Jin 8/12) */}
    {/* 말소증 파일 입력 — 포털/툴바 어디서 열려도 동작하도록 루트에 상시 마운트 */}
    <input ref={malsoInputRef} type="file" multiple accept="image/*,application/pdf" style={{display:'none'}} onChange={onMalsoFilesPicked}/>
    {/* 타이틀 배너 좌측 — [현황|장부|입금|정산서] 밑줄 탭만. 대상 칩·월·발행 액션은 아래 툴바 줄로 (Jin 8/26 — 헤더 줄 = 타이틀 + 탭). */}
    {!!isCancelAgency&&!!hdrSlotL&&ReactDOM.createPortal(
      <React.Fragment key="stl-header-left">{viewSeg}</React.Fragment>,
      hdrSlotL)}
    {/* 툴바 줄 — 좌: [대상 칩 ▾][‹월›] · 우: 발행 액션. 헤더에서 내려온 것 (Jin 8/26). */}
    {!!isCancelAgency&&<div style={isMobile
      ?{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:12}
      :{display:'flex',gap:8,alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap'}}>
      {/* 모바일 2행 고정 (Jin 8/26 재배치) — 1행 [거래처 검색 · 목록 ▾] 전폭 / 2행 [‹월› 좌끝 · 액션 우끝].
          1행 클러스터만 width:100% 로 줄을 끊고, 2행은 월과 액션이 한 줄에서 space-between.
          데스크탑은 기존 좌우 1행 그대로. */}
      <span style={isMobile
        ?{display:'flex',gap:6,alignItems:'center',width:'100%',minWidth:0}
        :{display:'inline-flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        {scopeChip}
        {!isMobile&&monthWidget}
      </span>
      {isMobile&&monthWidget}
      {/* whiteSpace nowrap — 좁은 화면에서 버튼 라벨('📄 정산서 발행' 등)이 두 줄로 깨지지 않게 */}
      <span style={isMobile
        ?{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',justifyContent:'flex-end',whiteSpace:'nowrap',marginLeft:'auto'}
        :{display:'inline-flex',gap:8,alignItems:'center',flexWrap:'wrap',whiteSpace:'nowrap'}}>
        {!!stlData&&<button className="btn btn-outline btn-sm" style={addItemBlocked?Object.assign({opacity:.5},actBtnSt):actBtnSt}
          title={addItemBlocked?'하위 업체(거래처)를 선택 후 사용':''} onClick={openAddItem}>+ 수기 추가</button>}
        {!!stlData&&<AdjustButton unitId={stlUnitId} accountId={stlAccountId} style={actBtnSt} showToast={showToast} onOpen={function(){setShowAdjust(true)}}/>}
        {!!stlData&&<button className="btn btn-outline btn-sm" disabled={!stlClientId||!!malsoBusy}
          title={stlClientId?'스캔본(이미지/PDF) 여러 장 선택 가능':'거래처(비가입 업체) 선택 후 사용'}
          style={!stlClientId?Object.assign({opacity:.5,cursor:'default'},actBtnSt):actBtnSt}
          onClick={function(){if(stlClientId&&!malsoBusy)openMalsoPicker()}}>
          {malsoBusy?(malsoBusy.done+'/'+malsoBusy.total+' 처리 중…'):'📷 말소증 등록'}
        </button>}
        {!!stlData&&stlCompanyId>0&&stlData.issueTaxInvoice!==0&&(function(){
          var st=(taxInv&&taxInv.record&&taxInv.record.status)||null;
          var stLabel=st==='issued'?'발행완료':st==='test'?'테스트발행':st==='failed'?'발행실패':st==='양식'?'양식기록':'미발행';
          return <button className="btn btn-outline btn-sm" style={actBtnSt} title={'세금계산서: '+stLabel} onClick={submitTaxInvoice}>🧾 세금계산서 발행</button>;
        })()}
        {!!stlData&&stlCompanyId>0&&stlData.issueTaxInvoice!==0&&<button className="btn btn-outline btn-sm" style={actBtnSt} onClick={function(){openPdf('/settlement-tax-invoice-pdf/'+stlAgencyId+'/'+stlCompanyId+'/'+stlMonth)}}>📑 양식</button>}
        {!!stlData&&stlCompanyId>0&&!!(taxInv&&taxInv.record)&&<button className="btn btn-outline btn-sm" style={Object.assign({color:'#dc2626'},actBtnSt)} onClick={cancelTaxInvoice}>발행 취소</button>}
        {!!issueAcc&&<button className="btn btn-blue btn-sm" style={actBtnSt} disabled={issuing} onClick={issueStatement}>{issuing?'발행 중…':'📄 정산서 발행'}</button>}
      </span>
    </div>}
    {/* 툴바 — admin/doc_agency 갈래 전용(기존 유지). cancel_agency 는 헤더의 칩·월·세그먼트·액션 포털이 전담하고
        이 줄 자체를 그리지 않는다 (거래처명 타이틀 = 대상 칩으로 대체, IA v2 ⑤). */}
    {!isCancelAgency&&<div style={{display:'flex',gap:8,alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap'}}>
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
      {/* admin/doc_agency: 대행업체 검색 (legacy 집계 흐름 유지) + 키보드 내비 (Jin 9/2, 콤보와 같은 UX) */}
      <div style={{position:'relative',minWidth:200}}>
        <input value={agencySearch} onChange={function(e){setAgencySearch(e.target.value);setAgencyHi(-1)}}
          onKeyDown={function(e){
            if(e.key==='Escape'){e.preventDefault();e.currentTarget.blur();return}
            if(e.key!=='ArrowDown'&&e.key!=='ArrowUp')return;
            if(!agencyHits.length)return;
            e.preventDefault();
            var last=agencyHits.length-1;
            setAgencyHi(e.key==='ArrowDown'?(agencyHi<0?0:Math.min(agencyHi+1,last)):(agencyHi<=0?0:agencyHi-1));
          }}
          onKeyUp={function(e){
            // Enter 는 keyup — 한글 IME 조합 확정 엔터는 keydown 에서 isComposing 으로 소비된다.
            if(e.key!=='Enter')return;
            if(agencyHits.length)pickAgency(agencyHits[agencyHi>=0&&agencyHi<agencyHits.length?agencyHi:0]);
            e.currentTarget.blur();
          }}
          placeholder="업체 검색..." style={{padding:'8px 12px',border:'1px solid #d0d5dd',borderRadius:8,fontSize:13,width:'100%'}}/>
        {agencySearch&&<div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:100,background:'#fff',border:'1px solid #d0d5dd',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.1)',maxHeight:200,overflowY:'auto',marginTop:2}}>
          {agencyHits.map(function(a,i){return(<div key={a.id} data-hi={i===agencyHi?'1':undefined} style={{padding:'8px 12px',cursor:'pointer',fontSize:13,borderBottom:'1px solid #f1f5f9',background:i===agencyHi?'#eff6ff':'#fff'}} onClick={function(){pickAgency(a)}} onMouseEnter={function(e){e.target.style.background='#f0f9ff'}} onMouseLeave={function(e){e.target.style.background=i===agencyHi?'#eff6ff':'#fff'}}>
            <span style={{fontWeight:700}}>{a.name}</span>{a.ceo?' · '+a.ceo:''}{a.bizNo?' · '+a.bizNo:''}
          </div>)})}
          {agencyHits.length===0&&<div style={{padding:10,color:'#94a3b8',fontSize:12}}>검색 결과 없음</div>}
        </div>}
      </div>
      {/* 무엇을 보고 있나 — 거래처명(볼드) */}
      {!!scopeTitle&&<span style={{fontSize:15,fontWeight:800,color:'#1B3A5C',maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{scopeTitle}</span>}
      {monthWidget}
      {/* admin/doc_agency 갈래 — 대행사 선택 후 조회하는 legacy 흐름 보존 */}
      <button className="btn btn-blue btn-sm" onClick={function(){
        var aid=stlAgencyId||'';
        if(!aid){showToast('업체를 선택하세요','err');return;}
        loadStl(aid,stlMonth,stlScope);
      }}>조회</button>
      {!!stlData&&<button className="btn btn-outline btn-sm" onClick={function(){setStlData(null);setStlAgencyId('');setAgencySearch('');setStlScope('')}}>← 전체 매출</button>}
      </div>
      {/* 액션 클러스터 — 입금 관리/장부 전환은 stlView 를 쓴다 (cancel_agency 의 세그먼트와 같은 축) */}
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',position:'relative'}}>
      {!!vPay&&<button className="btn btn-outline btn-sm" onClick={function(){setStlView('ledger')}}>← 장부로</button>}
      {!!stlData&&!vPay&&<button className="btn btn-green btn-sm" onClick={function(){setStlView('payments')}}>💰 입금 관리</button>}
      {!!stlData&&!vPay&&<AdjustButton unitId={stlUnitId} accountId={stlAccountId} showToast={showToast} onOpen={function(){setShowAdjust(true)}}/>}
      {!!stlData&&!vPay&&!!issueAcc&&<button className="btn btn-blue btn-sm" disabled={issuing} onClick={issueStatement}>{issuing?'발행 중…':'📄 정산서 발행'}</button>}
      {!vPay&&<button className="btn btn-outline btn-sm" title="더 보기" onClick={function(){setMenuOpen(!menuOpen)}}>⋯</button>}
      {!!menuOpen&&<>
        {/* 바깥 클릭 = 닫기 (backdrop). 메뉴보다 z-index 아래 */}
        <div onClick={function(){setMenuOpen(false)}} style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:200}}/>
        <div style={{position:'absolute',top:'100%',right:0,marginTop:4,zIndex:201,background:'#fff',border:'1px solid #e2e8f0',borderRadius:10,boxShadow:'0 8px 24px rgba(15,30,60,0.12)',minWidth:210,padding:'4px 0'}}>
          {/* 계정(그룹) 스코프도 accountId 로 전달 — 미전달 시 대행사 전체 금액이 찍혀 화면과 어긋남 (#304 잔여 갭) */}
          {!!stlData&&menuItem('📄 정산서 PDF',function(){openPdf('/settlement-pdf/'+stlAgencyId+'/'+stlMonth+(stlAccountId?('?accountId='+stlAccountId):(stlCompanyId?('?companyId='+stlCompanyId):(stlClientId?('?clientId='+stlClientId):''))))})}
          {!!stlData&&menuItem('+ 수기 추가',openAddItem,'',
            {disabled:addItemBlocked,sub:'하위 업체(거래처)를 선택 후 사용'})}
          {/* 말소증 등록 — walk-in 거래처 스코프에서만. 미선택 시 클릭하면 안내(#294) */}
          {!!stlData&&menuItem(malsoBusy?(malsoBusy.done+'/'+malsoBusy.total+' 처리 중…'):'📷 말소증 등록',function(){if(!malsoBusy)openMalsoPicker()},
            stlClientId?'스캔본(이미지/PDF) 여러 장 선택 가능':'',
            {disabled:!stlClientId,sub:'거래처(비가입 업체) 선택 후 사용'})}
          {/* 세금계산서 — 발행형(issueTaxInvoice!==0) 가입 거래처 선택 시에만. 비발행형/walk-in 은 숨김. */}
          {!!stlData&&stlCompanyId>0&&stlData.issueTaxInvoice!==0&&menuItem('🧾 세금계산서 발행',submitTaxInvoice)}
          {!!stlData&&stlCompanyId>0&&stlData.issueTaxInvoice!==0&&menuItem('📑 세금계산서 양식',function(){openPdf('/settlement-tax-invoice-pdf/'+stlAgencyId+'/'+stlCompanyId+'/'+stlMonth)})}
          {!!stlData&&stlCompanyId>0&&!!(taxInv&&taxInv.record)&&menuItem('발행 취소',cancelTaxInvoice)}
          {/* 거래처 발행형 표시 + 세금계산서 발행 상태 — 액션이 아니라 상태라 메뉴 하단 정보 줄로 */}
          {!!(stlData&&(stlCompanyId||stlClientId))&&<div style={{borderTop:'1px solid #f1f5f9',margin:'4px 0 0',padding:'8px 12px',fontSize:11.5,color:'#64748b'}}>
            <div>{stlData.issueTaxInvoice===0?'세금계산서 미발행 거래처':'세금계산서 발행 거래처'}</div>
            {!!taxInv&&(function(){
              var st=(taxInv.record&&taxInv.record.status)||null;
              var label=st==='issued'?'발행완료':st==='test'?'테스트발행':st==='failed'?'발행실패':st==='양식'?'양식기록':'미발행';
              var fg=st==='issued'?'#166534':st==='test'?'#1d4ed8':st==='failed'?'#b91c1c':st==='양식'?'#475569':'#9a3412';
              return <div style={{marginTop:2,fontWeight:700,color:fg}}>세금계산서: {label}</div>;
            })()}
          </div>}
        </div>
      </>}
      </div>
    </div>}

    {/* 정산서 발행 결과 — 공유 링크 복사 + 보관함 진입 (발행 = 소프트 마감, §6.4) */}
    {!!issued&&<div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:12,padding:'10px 14px',background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:8,fontSize:13,color:'#065f46'}}>
      <span>📄 {issued.name} · {stlMonth} 정산서 발행 완료{issued.shareUrl?' · 공유 링크 복사됨':''}</span>
      {!!issued.shareUrl&&<button className="btn btn-outline btn-sm" onClick={function(){copyText(issued.shareUrl,function(ok){if(ok)showToast('공유 링크를 복사했습니다');else showToast('복사 실패','err')})}}>🔗 링크 복사</button>}
      {/* 카톡 공유 — 클릭 핸들러에서 **동기** 호출(await 뒤면 사파리가 팝업 차단). 카드에 금액 없음.
          보냄 기록은 여기서 하지 않는다 — 기록 담당은 보관함의 [공유하기]/[카톡 공유] 다.
          발행 직후 배너는 "방금 만든 링크를 바로 보내는" 지름길일 뿐이라 상태를 건드리지 않는다. */}
      {!!issued.shareUrl&&<button className="btn btn-outline btn-sm" onClick={function(){
        if(ensureKakao()&&shareStatementLink({url:issued.shareUrl,title:periodTitle(issued.name,stlMonth),
          description:'AUTODEX 말소 대행 정산서',
          onError:function(m){showToast('카카오 공유 실패: '+m,'err')}}))return;
        showToast('카카오 공유를 쓸 수 없습니다 — 링크를 복사해 보내세요','err');
      }}>💬 카톡 공유</button>}
      <button className="btn btn-outline btn-sm" onClick={function(){setStlView('statements')}}>보관함에서 확인 →</button>
      <button onClick={function(){setIssued(null)}} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:16}}>✕</button>
    </div>}

    {/* ── (대상)×정산서 = 정산서 보관함 임베드. 전체 대상이면 대행사 전체 발행분(focusAccountId 0) ──
        전환 렌더가 아니라 본문 뷰라 헤더 칩·세그먼트가 그대로 남는다 (뒤로 갈 [← 정산] 버튼 불필요 → onBack 미전달) */}
    {!!vStmt&&<CancelAgencyStatements user={user} showToast={showToast} focusAccountId={ledgerAccId||0}/>}

    {/* 조회 중에는 기존 화면을 지우지 않고 흐리게만 — 월 이동 시 통째 깜빡임 방지(A2) */}
    {!vStmt&&<div style={stlLoading?{opacity:0.55,pointerEvents:'none'}:undefined}>
    {/* ── 전체 × 현황 = 미수 보드 (구 '미수' 탭 흡수) ── 행 클릭 = 대상만 이동(화면은 현황 유지) */}
    {!!showBoard&&!!stlData&&flowStrip}
    {!!showBoard&&<ReceivablesBoard
      agencyId={stlAgencyId||((user&&user.agencyId)||0)}
      month={stlMonth}
      showToast={showToast}
      accounts={accounts}
      unitsByAcc={unitsByAcc}
      scopeData={scopeData}
      payments={payments}
      monthBilled={grandTotal}
      monthPaid={totalPaid}
      reloadKey={boardKey}
      onPick={pickScope}
      onIssue={boardIssue}
      onPay={boardPay}
      onAssignPayment={patchPayScope}
      onChanged={reloadStl}/>}

    {!!stlData&&!showBoard&&<>
      {!vPay&&<>
      {/* 브로커 하위 드릴다운 복귀 — 유일하게 남긴 되돌아가기 진입점 (브레드크럼 대체, IA v2 ⑤) */}
      {!!parentAcc&&<button onClick={function(){pickScope('a:'+parentAcc.id)}}
        style={{display:'inline-flex',alignItems:'center',gap:4,marginBottom:8,background:'none',border:'none',padding:0,fontSize:12.5,fontWeight:700,color:'#2563eb',cursor:'pointer',fontFamily:'inherit'}}>← {parentAcc.name}</button>}

      {/* 요약 4칸 — 공통 flowStrip (보드와 동일) */}
      {flowStrip}

      {/* (계정)×현황 — 하위 업체 드릴다운 카드 (스트립 아래). 장부 뷰에서는 취합 장부가 본문이라 접는다 */}
      {!!(vOverview&&stlAccountId&&scopeAcc)&&(function(){
        // 하위 업체 미수 = 그 달 말 (전월미수 + 그 달 청구 − 그 달 입금) — 상단 스트립 '미수 (그 달 말)'·픽커와 같은 숫자.
        var subs=(unitsByAcc[scopeAcc.id]||[]).map(function(u){return Object.assign({},u,{balanceEnd:monthEndBal(u)})});
        var sq=subQ.trim().toLowerCase();
        if(sq)subs=subs.filter(function(u){return String(u.name||'').toLowerCase().indexOf(sq)>=0});
        var sk=subSort.key,sd=subSort.dir==='asc'?1:-1;
        subs.sort(function(a,b){
          if(sk==='name')return sd*String(a.name||'').localeCompare(String(b.name||''),'ko');
          return sd*(((a[sk]||0)-(b[sk]||0)));
        });
        // 금액 열 순서 — 모바일은 '미수'를 업체명 바로 옆으로 당긴다. 데스크탑 순서(전월미수→청구→입금→미수)
        // 그대로면 미수가 화면 밖이라 매번 오른쪽으로 밀어야 보이는데, 이 표를 여는 이유가 그 숫자다.
        var subCols=isMobile?['balanceEnd','monthOpening','monthCharged','monthPaid']:['monthOpening','monthCharged','monthPaid','balanceEnd'];
        return(<div style={{marginBottom:12,padding:14,background:'#fff',border:'1px solid #e5e7eb',borderRadius:8}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <span style={{fontSize:15,fontWeight:800}}>{scopeAcc.name}</span>
            {scopeAcc.kind==='broker'&&<span style={{fontSize:11,fontWeight:700,color:'#1d4ed8',background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:6,padding:'2px 6px'}}>대행사</span>}
            {scopeAcc.kind==='broker'&&<span style={{fontSize:12,color:'#64748b'}}>하위 {subs.length||scopeAcc.unitsCount||0}곳</span>}
          </div>
          {/* 대행사 × 현황의 본문 = 하위 업체 리스트. 차량별 취합 장부는 세그먼트 [장부] 로 (리스트 3중첩 방지) */}
          {scopeAcc.kind==='broker'&&<div style={{marginTop:10,borderTop:'1px solid #f1f5f9',paddingTop:8}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
              <span style={{fontSize:11,fontWeight:700,color:'#64748b'}}>하위 업체 — 행 클릭 시 해당 업체로 이동 · 헤더 클릭 = 정렬</span>
              <input value={subQ} onChange={function(e){setSubQ(e.target.value)}} placeholder="업체명 필터..."
                style={isMobile
                  ?{padding:'9px 10px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:12,width:'100%',boxSizing:'border-box'}
                  :{marginLeft:'auto',padding:'4px 10px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:12,width:180}}/>
              {!!sq&&<span style={{fontSize:11,color:'#94a3b8'}}>{subs.length}곳</span>}
            </div>
            {/* 상단 집계 스트립과 같은 4열 — 전월미수 / 그 달 청구 / 그 달 입금 / 미수 (그 달 말) (Jin 10/20). 캡 없이 쭉 나열. */}
            {/* 모바일 — 표는 그대로 두고 래퍼에서만 가로 스크롤 (페이지 가로 스크롤 금지) */}
            <div style={isMobile?hScroll:undefined}>
              <table style={isMobile
                ?{width:'100%',minWidth:520,borderCollapse:'collapse',fontSize:12.5}
                :{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
                {/* 헤더 상단 고정 — 스크롤 컨테이너(420px) 안에서 sticky */}
                {(function(){
                  // top:-pad = 스크롤 컨테이너(.dash-content padding — 모바일 12 / 데스크탑 24) 상단에 정확히 붙임 — 헤더 위 공백 제거.
                  // boxShadow 는 잔여 서브픽셀/오버스크롤 커버.
                  var subTh=function(align,key,label){
                    var on=subSort.key===key;
                    var st={padding:'5px 8px',fontSize:11,fontWeight:on?800:600,color:on?'#1B3A5C':undefined,cursor:'pointer',userSelect:'none',textAlign:align,borderBottom:'1px solid #e5e7eb',position:'sticky',top:-pad,background:'#fff',zIndex:1,boxShadow:'0 -'+pad+'px 0 0 #f0f2f5'};
                    // 업체명 열은 가로 스크롤 중에도 남는다 — 금액 열로 밀어도 어느 업체 줄인지 보이게 (모바일 한정)
                    if(isMobile&&key==='name')st=Object.assign({},st,{left:0,zIndex:2,whiteSpace:'nowrap'});
                    return(<th key={key} onClick={function(){setSubSort(function(p){return {key:key,dir:(p.key===key&&p.dir==='desc')?'asc':'desc'}})}}
                      title="클릭 = 정렬" style={st}>
                      {label}{on?(subSort.dir==='desc'?' ▼':' ▲'):''}
                    </th>);
                  };
                  var subLabels={
                    monthOpening:'전월미수',
                    monthCharged:isMobile?'청구':(isCurMonth?'이번 달 청구 · 예상':stlMonthNo+'월 청구'),
                    monthPaid:isMobile?'입금':((isCurMonth?'이번 달':stlMonthNo+'월')+' 입금'),
                    balanceEnd:isMobile?'미수':'미수 ('+stlMonthNo+'월 말)',
                  };
                  return(<thead><tr style={{color:'#64748b'}}>
                    {subTh('left','name','업체명')}
                    {/* 모바일 — 헤더가 2~3줄로 접히면 표가 세로로 부풀어 정렬 화살표까지 밀린다. 전체 문구는 위 스트립이 이미 들고 있다 */}
                    {subCols.map(function(k){return subTh('right',k,subLabels[k])})}
                  </tr></thead>);
                })()}
                <tbody>
                  {subs.map(function(u){
                    var hasFlow=u.monthOpening!==undefined&&u.monthOpening!==null;
                    var cell={
                      monthOpening:<td key="o" style={{padding:'6px 8px',textAlign:'right',color:'#334155'}}>{hasFlow?fmt(u.monthOpening)+'원':'-'}</td>,
                      monthCharged:<td key="c" style={{padding:'6px 8px',textAlign:'right',color:'#334155'}}>{hasFlow?fmt(u.monthCharged)+'원':'-'}</td>,
                      monthPaid:<td key="p" style={{padding:'6px 8px',textAlign:'right',color:'#059669'}}>{hasFlow?fmt(u.monthPaid)+'원':'-'}</td>,
                      balanceEnd:<td key="b" style={{padding:'6px 8px',textAlign:'right',fontWeight:700,color:u.balanceEnd>0?'#dc2626':(u.balanceEnd<0?'#059669':'#94a3b8')}}>{fmt(Math.abs(u.balanceEnd))}원</td>,
                    };
                    return(<tr key={u.id} onClick={function(){pickScope('u:'+u.id)}} style={{cursor:'pointer',borderBottom:'1px solid #f8fafc'}}>
                      <td style={isMobile?{padding:'10px 8px',whiteSpace:'nowrap',position:'sticky',left:0,background:'#fff',zIndex:1}:{padding:'6px 8px'}}>{u.name}</td>
                      {subCols.map(function(k){return cell[k]})}
                    </tr>);
                  })}
                  {subs.length===0&&<tr><td colSpan={5} style={{padding:10,color:'#94a3b8',fontSize:12}}>하위 업체 없음</td></tr>}
                  {subs.length>0&&(function(){
                    var accFlow=scopeAcc.monthOpening!==undefined&&scopeAcc.monthOpening!==null;
                    var accOut=monthEndBal(scopeAcc);
                    var cell={
                      monthOpening:<td key="o" style={{padding:'6px 8px',textAlign:'right'}}>{accFlow?fmt(scopeAcc.monthOpening)+'원':'-'}</td>,
                      monthCharged:<td key="c" style={{padding:'6px 8px',textAlign:'right'}}>{accFlow?fmt(scopeAcc.monthCharged)+'원':'-'}</td>,
                      monthPaid:<td key="p" style={{padding:'6px 8px',textAlign:'right',color:'#059669'}}>{accFlow?fmt(scopeAcc.monthPaid)+'원':'-'}</td>,
                      balanceEnd:<td key="b" style={{padding:'6px 8px',textAlign:'right',color:accOut>0?'#dc2626':(accOut<0?'#059669':'#334155')}}>{fmt(Math.abs(accOut))}원</td>,
                    };
                    return(<tr style={{background:'#f0f4f8',fontWeight:700}}>
                      <td style={isMobile?{padding:'10px 8px',whiteSpace:'nowrap',position:'sticky',left:0,background:'#f0f4f8',zIndex:1}:{padding:'6px 8px'}}>합계 (계정 전체)</td>
                      {subCols.map(function(k){return cell[k]})}
                    </tr>);
                  })()}
                </tbody>
              </table>
            </div>
          </div>}
        </div>);
      })()}

      {/* ── 장부 화면 안 탭 [월별 정산표 | 일별 | 주별 | 월별] ──
          첫 탭 = 아래 장부 표(기존 그대로). 나머지 = 일계표 그룹 뷰가 그 자리를 통째로 차지.
          헤더의 뷰 세그먼트와 같은 밑줄 탭 문법(CaHeaderTabs) — 여긴 본문이라 헤더 슬롯 CSS 는 안 탄다.
          세금계산서 발행 스위치(TaxMonthSwitch) — 거래처(단위) 스코프에서만 이 탭 줄 우측 끝에 붙인다 (Jin 9/17). */}
      {!!showLedgerTable&&<div style={{marginBottom:10}}>
        <div style={isMobile?hScroll:{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <CaHeaderTabs isMobile={isMobile} items={vjTabs.map(function(t){
            return {label:t[1],on:ledgerTab===t[0],onClick:function(){setLedgerTab(t[0])}};
          })}/>
          {!isMobile&&!!stlUnitId&&<span style={{marginLeft:'auto'}}>
            <TaxMonthSwitch unitId={stlUnitId} month={stlMonth} taxMonth={stlData&&stlData.taxMonth} showToast={showToast} onChanged={reloadStl}/>
          </span>}
        </div>
        {isMobile&&!!stlUnitId&&<div style={{display:'flex',justifyContent:'flex-end',marginTop:6}}>
          <TaxMonthSwitch unitId={stlUnitId} month={stlMonth} taxMonth={stlData&&stlData.taxMonth} showToast={showToast} onChanged={reloadStl}/>
        </div>}
      </div>}

      {/* ── [일별]/[주별] = 일계표 그룹 뷰 (풀폭). 한 인스턴스를 공유해 둘 사이 왕복은 무요청.
          거래처 스코프는 장부와 같은 좌표(jScopeQs) — 서버가 같은 우선순위로 거른다. ── */}
      {!!showLedgerTable&&ledgerTab!=='ledger'&&<CancelAgencyJournal agencyId={stlAgencyId||((user&&user.agencyId)||0)}
        month={stlMonth} mode={ledgerTab} scopeQs={jScopeQs} scopeLabel={scopeTitle}
        showToast={showToast} openPdf={openPdf} presenceBadge={itemPresenceBadge}/>}

      {/* 장부형 상세 표 = '장부' 뷰 [월별 정산표] 탭 (admin/doc_agency 는 화면 축이 없어 현황 = 장부).
          전체·계정 취합 스코프에서는 거래처 컬럼 노출. 컬럼이 많아 가로 스크롤. */}
      {!!showLedgerTable&&ledgerTab==='ledger'&&<div style={isMobile?hScroll:{overflowX:'auto'}}>
      {/* minWidth — 재교부 3분할(M1)로 금액 열이 9 → 11 개가 됐다. 좁히면 헤더가 두 줄로 접혀 장부가 안 읽힌다 */}
      <table style={{width:'100%',minWidth:1400,borderCollapse:'collapse',fontSize:12}}>
        <thead><tr style={{background:'#1e3a5f',color:'#fff'}}>
          {headers.map(function(h,hi){return(<th key={h+hi} style={{padding:'8px 6px',fontSize:11,fontWeight:700,whiteSpace:'nowrap',textAlign:rightCols.indexOf(h)>=0?'right':'left'}}>{h}</th>)})}
        </tr></thead>
        <tbody>
          {(function(){
            var rows=[],no=0;
            groupByDate(items).forEach(function(g,gi){
              var gDate=g.date||'';
              g.items.forEach(function(it,ii){
                no+=1;
                var unc=unclassifiedCost(it);
                var lgR=legacyReissueSum(it);
                // 순서 변경 — 계정(a:) 취합 장부는 여러 업체가 섞여 순서에 의미가 없다(addItemBlocked 와 같은 조건).
                // 한 그룹에 행이 하나뿐이면 옮길 자리가 없어 숨긴다.
                var canMove=!addItemBlocked&&g.items.length>1;
                var canDrag=canMove&&!isMobile;
                rows.push(<tr key={'i'+it.id} draggable={canDrag||undefined}
                  onDragStart={canDrag?function(e){
                    dragRef.current={id:it.id,date:gDate};setDragId(it.id);
                    if(e.dataTransfer){e.dataTransfer.effectAllowed='move';try{e.dataTransfer.setData('text/plain',String(it.id))}catch(err){}}
                  }:undefined}
                  onDragOver={canDrag?function(e){
                    var src=dragRef.current;
                    if(!src||src.id===it.id)return;
                    if(src.date!==gDate){if(e.dataTransfer)e.dataTransfer.dropEffect='none';return;}
                    e.preventDefault();
                    if(e.dataTransfer)e.dataTransfer.dropEffect='move';
                    if(dropId!==it.id)setDropId(it.id);
                  }:undefined}
                  onDrop={canDrag?function(e){dropOnRow(g.items,gDate,it,e)}:undefined}
                  onDragEnd={canDrag?dragEnd:undefined}
                  style={Object.assign({background:no%2===0?'#f8fafc':'#fff',borderBottom:'1px solid #e5e7eb',cursor:'pointer'},
                    dragId===it.id?{opacity:.5}:null,
                    dropId===it.id?{boxShadow:'inset 0 2px 0 #1B3A5C'}:null)}
                  onClick={function(){startEditItem(it)}}>
                  <td style={isMobile?{padding:'10px 6px',textAlign:'center'}:{padding:'6px',textAlign:'center'}}>{itemPresenceBadge(it.id)}{no}</td>
                  <td style={{padding:'6px',whiteSpace:'nowrap'}}>{(it.date||'').slice(5)||'-'}</td>
                  {/* 모바일 nowrap — 어차피 래퍼가 가로 스크롤이라 접어도 얻는 게 없고, 한 행이 2~3줄로 부풀어 장부가 안 읽힌다 */}
                  {showCompanyCol&&<td style={isMobile?nowrapCell:{padding:'6px',fontSize:11,color:'#475569'}}>{it.companyName||<span style={{color:'#cbd5e1'}}>미지정</span>}</td>}
                  <td style={isMobile?nowrapCellSub:{padding:'6px',fontSize:11,color:'#64748b'}}>{it.carType||'-'}</td>
                  <td style={{padding:'6px',fontWeight:700,whiteSpace:'nowrap'}}>{it.carNo||'-'}</td>
                  <td style={{padding:'6px',fontFamily:'monospace',fontSize:11,color:'#666'}}>{it.vin?it.vin.slice(-8):(it.vinLast6||'-')}</td>
                  {/* 분리 컬럼 이전 행은 미분류 실비를 등록세 칸에 괄호 표기 — 컬럼 합과 소계 불일치 방지 */}
                  <td style={{padding:'6px',textAlign:'right',color:unc>0?'#94a3b8':'#334155'}}>{unc>0?'('+fmt(unc)+')':amt(it.registrationTax)}</td>
                  <td style={{padding:'6px',textAlign:'right'}}>{amt(it.stampDuty)}</td>
                  <td style={{padding:'6px',textAlign:'right'}}>{amt(it.serviceFee)}</td>
                  {showVat&&<td style={{padding:'6px',textAlign:'right'}}>{amt(it.vatAmount)}</td>}
                  {/* 기타 계열이 재교부 그룹 앞 — 엑셀 장부 열 순서 */}
                  <td style={{padding:'6px',textAlign:'right'}}>{amt(it.otherFee)}</td>
                  <td style={{padding:'6px',textAlign:'right'}}>{amt(it.otherTaxableFee)}</td>
                  <td style={{padding:'6px',textAlign:'right'}} title={it.extraServiceNote||''}>{amt(it.extraServiceFee)}</td>
                  {/* 재교부 그룹 3칸 — 분해 전(구 재교부/원부) 행은 합계만 수수료 칸에 괄호로, 나머지는 '-' */}
                  <td style={{padding:'6px',textAlign:'right'}}>{lgR?'-':amt(it.reissueStampDuty)}</td>
                  <td style={{padding:'6px',textAlign:'right',color:(lgR||it.reissueServiceFee)?'#92400e':'#334155'}}>{lgR?'('+fmt(lgR)+')':amt(it.reissueServiceFee)}</td>
                  <td style={{padding:'6px',textAlign:'right'}}>{lgR?'-':amt(it.reissueVat)}</td>
                  <td style={{padding:'6px',textAlign:'right',fontWeight:700}}>{fmt(it.subtotal)}</td>
                  <td style={isMobile?{padding:'6px',fontSize:10,color:'#64748b',whiteSpace:'nowrap'}:{padding:'6px',fontSize:10,color:'#64748b'}}><JobKindBadge kind={it.jobKind}/>{it.note}{it.noteDetail?' '+it.noteDetail:''}{it.extraServiceNote?' / '+it.extraServiceNote:''}</td>
                  <td style={{padding:'6px',textAlign:'center'}}>{malsoDocBtn(it)}</td>
                  {/* 구청 칸 우측에 ▲▼ — 같은 날짜 그룹 안 순서 변경 (행 클릭=수정 과 겹치지 않게 stopPropagation) */}
                  <td style={isMobile?nowrapCell:{padding:'6px',fontSize:11,color:'#475569'}}>
                    <span style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:6}}>
                      <span>{it.district||'-'}</span>
                      {!!canMove&&<span style={{display:'flex',gap:2}}>
                        <button type="button" title="위로" aria-label="위로" disabled={ii===0||reorderBusy}
                          onClick={function(e){e.stopPropagation();moveItem(g.items,ii,-1)}}
                          style={moveBtnSt(ii===0||reorderBusy)}>▲</button>
                        <button type="button" title="아래로" aria-label="아래로" disabled={ii===g.items.length-1||reorderBusy}
                          onClick={function(e){e.stopPropagation();moveItem(g.items,ii,1)}}
                          style={moveBtnSt(ii===g.items.length-1||reorderBusy)}>▼</button>
                      </span>}
                    </span>
                  </td>
                </tr>);
              });
              // 일자 그룹 소계 — 장부의 '합계' 컬럼
              // 모바일 — 라벨을 왼쪽으로. 우측 정렬이면 소계 열 바로 앞(=화면 밖 800px 지점)에 찍혀
              // 가로 스크롤 전에는 회색 띠만 보이고 무슨 행인지 알 수 없다.
              rows.push(<tr key={'g'+gi} style={{background:'#eef2f7',fontWeight:700,color:'#334155',borderBottom:'1px solid #e5e7eb'}}>
                <td colSpan={subtotalIdx} style={{padding:'5px 6px',textAlign:isMobile?'left':'right',fontSize:11}}>{(g.date||'').slice(5)||'날짜없음'} 합계 {g.items.length}건</td>
                <td style={{padding:'5px 6px',textAlign:'right'}}>{fmt(g.subtotal)}</td>
                <td colSpan={3}></td>
              </tr>);
            });
            return rows;
          })()}
          <tr style={{background:'#f0f4f8',fontWeight:700}}>
            <td colSpan={showCompanyCol?6:5} style={{padding:'8px 6px',textAlign:isMobile?'left':'right'}}>합계 {stlData.count}대</td>
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(sumBy(items,'registrationTax'))}{totalUnclassified>0&&<div style={{fontSize:10,fontWeight:400,color:'#94a3b8'}}>({fmt(totalUnclassified)})</div>}</td>
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(sumBy(items,'stampDuty'))}</td>
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(sumBy(items,'serviceFee'))}</td>
            {showVat&&<td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(stlData.totalVat)}</td>}
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(stlData.totalOther)}</td>
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(sumBy(items,'otherTaxableFee'))}</td>
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(stlData.totalExtra)}</td>
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(sumBy(items,'reissueStampDuty'))}</td>
            {/* 재교부수수료 합계는 legacy 미분해분(구 재교부+구 원부)까지 더한다 — 원부 열이 사라졌으므로
                여기 안 더하면 옛 행의 실제 청구액이 합계에서 증발한다 (신규 행은 두 칸이 0 → 이중계상 없음).
                utils/statementPdf.js detailTable 의 합계 규칙과 동일. */}
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(sumBy(items,'reissueServiceFee')+sumBy(items,'reissueFee')+sumBy(items,'wonbuFee'))}</td>
            <td style={{padding:'8px 6px',textAlign:'right'}}>{fmt(totalReissueVat)}</td>
            <td style={{padding:'8px 6px',textAlign:'right',color:'#059669',fontSize:14}}>{fmt(grandTotal)}</td>
            <td colSpan={3}></td>
          </tr>
          {/* 업무별 소계 — 말소 외 행이 있는 달에만 (말소만 있는 달은 기존 화면 그대로) */}
          {items.some(function(i){return jobKindOf(i.jobKind)!=='말소'})&&JOB_KINDS.map(function(k){
            var ks=items.filter(function(i){return jobKindOf(i.jobKind)===k});
            if(!ks.length)return null;
            return (<tr key={'jk'+k} style={{background:'#f8fafc',color:'#334155',borderBottom:'1px solid #e5e7eb'}}>
              <td colSpan={subtotalIdx} style={{padding:'5px 6px',textAlign:isMobile?'left':'right',fontSize:11}}>{k} {ks.length}대</td>
              <td style={{padding:'5px 6px',textAlign:'right',fontWeight:700}}>{fmt(sumBy(ks,'subtotal'))}</td>
              <td colSpan={3}></td>
            </tr>);
          })}
        </tbody>
      </table>
      </div>}
      {/* 수기 조정 목록 — 그 달 조정이 있을 때만 (없으면 아무것도 그리지 않아 화면 불변) */}
      {!!showLedgerTable&&ledgerTab==='ledger'&&<SettlementAdjustList adjustments={adjustments} showUnit={!stlUnitId} showToast={showToast} onChanged={reloadStl}/>}

      </>}

      {/* ── (대상)×입금 = 입금 관리 — 정산 상태 · 입금 이력 · 등록 폼. 전체 대상이면 (미지정) 건까지 전부. ── */}
      {!!vPay&&flowStrip}
      {!!vPay&&<div style={{marginTop:4,padding:isMobile?12:16,background:'#f8fafc',borderRadius:8,border:'1px solid #e5e7eb'}}>
        {/* 타이틀+폼 = sticky 상단 블록 (top:-pad 로 .dash-content 패딩 상쇄). 리스트는 페이지 스크롤로 쭉.
            모바일은 폼이 세로 스택이라 블록이 화면 절반을 먹는다 → sticky 해제(static). */}
        <div ref={payHeadRef} style={isMobile
          ?{background:'#f8fafc'}
          :{position:'sticky',top:-pad,zIndex:3,background:'#f8fafc',boxShadow:'0 -'+pad+'px 0 0 #f0f2f5'}}>
        <div style={{fontSize:15,fontWeight:800,color:'#1B3A5C',marginBottom:2,paddingTop:2}}>💰 입금 관리</div>
        <div style={{fontSize:12,color:'#94a3b8',marginBottom:12}}>{monthLabel()} · 입금 이력 조회 · 거래처 재지정(✎) · 신규 등록 — 대상은 헤더의 거래처 칩</div>
        <div style={{background:'#f8fafc',paddingBottom:10,borderBottom:'1px solid #e5e7eb',marginBottom:12}}>
        {/* 모바일 — 각 필드 100% 폭 세로 스택 (데스크탑은 기존 가로 배치) */}
        <div style={{display:'flex',gap:8,alignItems:isMobile?'stretch':'flex-end',marginTop:0,flexWrap:'wrap'}}>
          {/* #303 — 입금 귀속 거래처. 기본값 = 현재 조회 스코프, (미지정) 선택 = 나중에 지정 (#304) */}
          {/* 콤보 자체 minWidth 220 — 칸이 그보다 좁으면 옆(입금일)을 침범한다 */}
          <div style={isMobile?{width:'100%'}:{width:230}}><label style={{...ls,marginTop:0}}>거래처</label>
            <ClientScopeCombo value={effPayScope} data={scopeData} onChange={setPayScope} emptyLabel="(미지정)" placeholder="거래처 검색..."/></div>
          <div style={isMobile?{width:'100%'}:{width:150}}><label style={{...ls,marginTop:0}}>입금일</label><input type="date" style={isSm} value={payForm.date} onChange={function(e){setPayForm(Object.assign({},payForm,{date:e.target.value}))}}/></div>
          <div style={isMobile?{width:'100%'}:{width:140}}><label style={{...ls,marginTop:0}}>금액</label><input ref={payAmtRef} type="number" style={isSm} value={payForm.amount} placeholder="0" onChange={function(e){setPayForm(Object.assign({},payForm,{amount:e.target.value}))}}/></div>
          <div style={isMobile?{width:'100%'}:{flex:1,minWidth:160}}><label style={{...ls,marginTop:0}}>메모 (선택)</label><input style={isSm} value={payForm.memo} placeholder="예: 계좌이체" onChange={function(e){setPayForm(Object.assign({},payForm,{memo:e.target.value}))}}/></div>
          {/* 툴바 [💰 입금 추가] 는 이 폼으로 이동만 — 실제 등록은 이 버튼 (라벨 중복 방지) */}
          <button className="btn btn-green btn-sm" style={isMobile?{width:'100%',height:44,justifyContent:'center'}:undefined} onClick={addPayment}>💰 입금 등록</button>
        </div>
        {stlData.agency.bankName&&<div style={{fontSize:12,color:'#64748b',marginTop:10,paddingBottom:8}}>입금계좌: {stlData.agency.bankName} {stlData.agency.accountHolder} {stlData.agency.accountNumber}</div>}
        </div>
        </div>

        {/* 모바일 — 5열(입금일·거래처·메모·금액·액션) 표는 390px 에서 minWidth 700 가로 스크롤이 되어
            정작 금액이 화면 밖으로 나갔고, 편집 모드는 그 안에 입력 3개+버튼 3개가 더 들어갔다.
            → 카드 스택으로 전환. 금액은 카드 우측 정렬로 유지하고 편집은 카드가 세로 폼으로 펼쳐진다. */}
        {!!isMobile&&<div>
          {payments.map(function(p){
            var un=!(p.companyId||p.clientId)&&p.unassigned!==false;
            var ed=!!editPay&&editPay.id===p.id;
            var startEdit=function(){startEditPay(p)};
            return(<div key={p.id} style={{border:'1px solid #e5e7eb',borderRadius:8,background:'#fff',padding:12,marginBottom:8}}>
              {ed
                ? (<div>
                    <label style={{...ls,marginTop:0}}>거래처</label>
                    <ClientScopeCombo value={editPay.scope} data={scopeData}
                      onChange={function(v){setEditPay(Object.assign({},editPay,{scope:v}))}} emptyLabel="(미지정)" placeholder="거래처 검색..."/>
                    <label style={ls}>입금일</label>
                    <input type="date" style={isSm} value={editPay.date} onChange={function(e){setEditPay(Object.assign({},editPay,{date:e.target.value}))}}/>
                    <label style={ls}>금액</label>
                    <input type="number" style={Object.assign({},isSm,{textAlign:'right'})} value={editPay.amount} onChange={function(e){setEditPay(Object.assign({},editPay,{amount:e.target.value}))}}/>
                    <label style={ls}>메모</label>
                    <input style={isSm} value={editPay.memo} placeholder="예: 계좌이체" onChange={function(e){setEditPay(Object.assign({},editPay,{memo:e.target.value}))}}/>
                    <div style={{display:'flex',gap:8,marginTop:12}}>
                      <button className="btn btn-red-soft btn-sm" style={{minHeight:40,marginRight:8}} onClick={function(){deletePayment(p)}}>삭제</button>
                      <button className="btn btn-sm" style={{flex:1,minHeight:40,justifyContent:'center'}} onClick={function(){setEditPay(null)}}>취소</button>
                      <button className="btn btn-blue btn-sm" style={{flex:1,minHeight:40,justifyContent:'center'}} onClick={saveEditPay}>저장</button>
                    </div>
                  </div>)
                : (<div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,color:'#94a3b8'}}>{p.date||'-'}</div>
                      <div style={{marginTop:2,fontSize:13,fontWeight:700,color:'#1e293b',wordBreak:'break-all'}}>
                        {un
                          ? <span style={{color:'#b45309',background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:6,padding:'1px 6px',fontSize:11,fontWeight:700}}>(미지정)</span>
                          : (p.scopeName||'-')}
                      </div>
                      {!!p.memo&&<div style={{marginTop:3,fontSize:12,color:'#64748b',wordBreak:'break-all'}}>{p.memo}</div>}
                    </div>
                    <div style={{flexShrink:0,textAlign:'right'}}>
                      <div style={{fontSize:15,fontWeight:800,color:'#059669',whiteSpace:'nowrap'}}>{fmt(p.amount)}원</div>
                      <button title="수정" onClick={startEdit}
                        style={{marginTop:2,background:'none',border:'1px solid #e2e8f0',borderRadius:8,width:40,height:40,color:'#64748b',fontSize:14,cursor:'pointer',padding:0,lineHeight:1}}>✎</button>
                    </div>
                  </div>)}
            </div>);
          })}
          {payments.length===0&&<div style={{border:'1px solid #e5e7eb',borderRadius:8,background:'#fff',padding:'20px 10px',textAlign:'center',color:'#94a3b8',fontSize:12}}>입금 내역 없음</div>}
        </div>}

        {!isMobile&&<div style={{border:'1px solid #e5e7eb',background:'#fff'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,background:'#fff'}}>
          <thead><tr>
            {/* 헤더 sticky = 상단 고정 블록(payHeadH) 바로 아래 — 업체 리스트와 동일 패턴. (데스크탑 전용 표) */}
            {['입금일','거래처','메모','금액',''].map(function(h){return(<th key={h} style={{padding:'6px 10px',fontSize:11,fontWeight:600,whiteSpace:'nowrap',textAlign:h==='금액'?'right':'left',position:'sticky',top:(payHeadH?payHeadH-pad:0),zIndex:1,background:'#f1f5f9',color:'#64748b'}}>{h}</th>)})}
          </tr></thead>
          <tbody>
            {payments.map(function(p){
              // 미지정 (#304 — 눈에 띄게 + 재지정 진입점). 대행업체 이름으로 받은 통합 입금은
              // 좌표가 없어도 계정에 귀속돼 있으므로 제외 (서버 unassigned, 2026-08-17. 구 서버는 키 부재).
              var un=!(p.companyId||p.clientId)&&p.unassigned!==false;
              var ed=!!editPay&&editPay.id===p.id;
              var startEdit=function(){startEditPay(p)};
              return(<tr key={p.id} style={{borderTop:'1px solid #f1f5f9'}}>
              <td style={{padding:'6px 10px',whiteSpace:'nowrap'}}>
                {ed
                  ? <input type="date" style={Object.assign({},isSm,{width:140})} value={editPay.date}
                      onChange={function(e){setEditPay(Object.assign({},editPay,{date:e.target.value}))}}/>
                  : (p.date||'-')}
              </td>
              <td style={{padding:'6px 10px',minWidth:150}}>
                {ed
                  ? <div style={{minWidth:180}}><ClientScopeCombo value={editPay.scope} data={scopeData}
                      onChange={function(v){setEditPay(Object.assign({},editPay,{scope:v}))}} emptyLabel="(미지정)" placeholder="거래처 검색..."/></div>
                  : <span onClick={startEdit} title="입금 수정" style={{cursor:'pointer'}}>
                      {un
                        ? <span style={{color:'#b45309',background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:6,padding:'1px 6px',fontSize:11,fontWeight:700}}>(미지정)</span>
                        : <span>{p.scopeName||''}</span>}
                    </span>}
              </td>
              <td style={{padding:'6px 10px',color:'#64748b'}}>
                {ed
                  ? <input style={isSm} value={editPay.memo} placeholder="예: 계좌이체"
                      onChange={function(e){setEditPay(Object.assign({},editPay,{memo:e.target.value}))}}/>
                  : (p.memo||'')}
              </td>
              <td style={{padding:'6px 10px',textAlign:'right',fontWeight:700,color:'#059669'}}>
                {ed
                  ? <input type="number" style={Object.assign({},isSm,{textAlign:'right'})} value={editPay.amount}
                      onChange={function(e){setEditPay(Object.assign({},editPay,{amount:e.target.value}))}}/>
                  : fmt(p.amount)}
              </td>
              <td style={{padding:'6px 10px',whiteSpace:'nowrap',textAlign:'right'}}>
                {ed
                  ? <span style={{display:'inline-flex',gap:4,alignItems:'center'}}>
                      <button className="btn btn-blue btn-sm" onClick={saveEditPay}>저장</button>
                      <button className="btn btn-sm" onClick={function(){setEditPay(null)}}>취소</button>
                      <button className="btn btn-red-soft btn-sm" style={{marginLeft:12}} onClick={function(){deletePayment(p)}}>삭제</button>
                    </span>
                  : <button title="수정" onClick={startEdit} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:12}}>✎</button>}
              </td>
            </tr>)})}
            {payments.length===0&&<tr><td colSpan={5} style={{padding:'14px 10px',textAlign:'center',color:'#94a3b8'}}>입금 내역 없음</td></tr>}
          </tbody>
        </table>
        </div>}

      </div>}
    </>}
    {!stlData&&!showBoard&&<div style={{textAlign:'center',padding:40,color:'#94a3b8'}}>업체와 월을 선택 후 [조회] 버튼을 누르세요</div>}
    </div>}

    {/* 원장 거래 내역 드로어 — 흐름 4줄의 금액 탭 진입 (§6.3) */}
    {!!drawer&&<LedgerDrawer accountId={drawer.accountId} unitId={drawer.unitId||undefined} month={drawer.month||stlMonth}
      seg={drawer.seg||''} label={drawer.label} onClose={function(){setDrawer(null)}}/>}

    {/* 구청 입력 제안 — 같은 달 항목에 이미 쓰인 값 */}
    <datalist id="stl-districts">{districts.map(function(d){return <option key={d} value={d}/>})}</datalist>

    {/* 말소증 등록 결과 요약 — 건별 상태. ⚠️(확인 필요) 행은 클릭해 바로 보정. */}
    {malsoResults&&<div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}} onClick={closeMalsoResults}>
      <div style={isMobile
        ?{background:'#fff',borderRadius:12,padding:16,width:'100%',maxWidth:'94vw',maxHeight:'88vh',overflowY:'auto'}
        :{background:'#fff',borderRadius:12,padding:24,width:520,maxWidth:'92vw',maxHeight:'90vh',overflowY:'auto'}} onClick={function(e){e.stopPropagation()}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:700}}>말소증 등록 결과</h3>
          <button onClick={closeMalsoResults} style={isMobile
            ?{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#94a3b8',width:40,height:40,lineHeight:1}
            :{background:'none',border:'none',fontSize:18,cursor:'pointer',color:'#94a3b8'}}>✕</button>
        </div>
        {(function(){
          var c=function(s){return malsoResults.filter(function(r){return r.status===s}).length};
          return <p style={{margin:'0 0 12px',fontSize:12,color:'#64748b'}}>총 {malsoResults.length}건 · ✅ {c('ok')} · ⚠️ {c('warn')} · 🔁 {c('dup')} · ❌ {c('fail')}</p>;
        })()}
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,border:'1px solid #e5e7eb'}}>
          <thead><tr style={{background:'#f1f5f9',color:'#64748b'}}>
            {['파일','차량번호','상태'].map(function(h){return(<th key={h} style={{padding:'6px 10px',fontSize:11,fontWeight:600,textAlign:'left'}}>{h}</th>)})}
          </tr></thead>
          <tbody>
            {malsoResults.map(function(r,i){
              var canFix=r.status==='warn'&&r.itemId>0;
              var label=r.status==='ok'?'✅ 완료':r.status==='warn'?'⚠️ 확인 필요':r.status==='dup'?'🔁 중복':'❌ 실패';
              var color=r.status==='ok'?'#059669':r.status==='warn'?'#b45309':r.status==='dup'?'#475569':'#dc2626';
              var detail=r.status==='fail'?(r.error||''):(r.warnings||[]).join(' / ');
              return(<tr key={r.name+i} onClick={canFix?function(){openMalsoResultItem(r)}:undefined} style={{borderTop:'1px solid #f1f5f9',cursor:canFix?'pointer':'default'}}>
                <td style={{padding:'6px 10px',wordBreak:'break-all'}}>{r.name}</td>
                <td style={{padding:'6px 10px',fontWeight:700}}>{r.carNo||'-'}</td>
                <td style={{padding:'6px 10px',color:color}}>
                  {label}{canFix&&<span style={{marginLeft:6,fontSize:11,color:'#2563eb'}}>수정 →</span>}
                  {detail&&<div style={{fontSize:11,fontWeight:400,color:'#94a3b8',marginTop:2}}>{detail}</div>}
                </td>
              </tr>);
            })}
          </tbody>
        </table>
        <div style={{display:'flex',gap:8,marginTop:16}}>
          <button className="btn btn-blue" style={{flex:1,justifyContent:'center'}} onClick={closeMalsoResults}>닫기</button>
        </div>
      </div>
    </div>}

    {/* 정산 항목 수정 — 우측 드로어(4섹션 그리드). 구 모달은 12칸이 세로로 나열돼 읽히지 않았다. */}
    {editItem&&(function(){
      var issue=!!editItem.issueTaxInvoice;
      var apply=function(patch){setEditItem(Object.assign({},editItem,patch))};
      // W8 — 요청과 연결된 항목(requestId>0)은 부가서비스가 요청 부가서비스 편집기(RequestServicesEditor)로
      // 파생된다(서버가 PUT /settlement-items/:id 의 extraServiceFee/Note 를 무시). 미연결 항목만 칩 콤보.
      var linked=(parseInt(editItem.requestId,10)||0)>0;
      return (<SettlementItemDrawer mode="edit" isMobile={isMobile} title="정산 항목 수정" subtitle={(scopeTitle?scopeTitle+' · ':'')+monthLabel()}
        presenceBanner={itemPresenceBanner(editItem.id)} linked={linked}
        form={editItem} onChange={apply} onSubmit={saveEditItem} onClose={function(){setEditItem(null)}} onDelete={deleteEditItem}
        issue={issue} preview={settlementPreviewOf(editItem,issue,linked?linkedExtraTaxable:0)} legacyReissueSum={legacyReissueSum(editItem)}
        districtListId="stl-districts" workTypeField={workTypeField(editItem,apply,is)}
        noteCombo={<PresetCombo key={'nt'+editItem.id} presets={NOTE_PRESETS} is={is} placeholder="비고 직접 입력" value={editItem.note} onChange={function(v){apply({note:v})}}/>}
        extraCombo={linked
          ? <RequestServicesEditor requestId={editItem.requestId} agencyId={parseInt(stlAgencyId)||0} showFee showToast={showToast} onSaved={onServicesSaved} onRowsChange={onLinkedRowsChange}/>
          : <ExtraServiceCombo key={'es'+editItem.id} catalog={extraCatalog} value={editItem.extraServiceNote} onChange={function(v){apply({extraServiceNote:v})}}/>}/>);
    })()}

    {/* 수기 항목 추가 — 같은 드로어의 add 모드. 요청과 무관하게 생성되는 행이라 항상 미연결(칩 콤보). */}
    {showAddItem&&(function(){
      var issue=addItemForm.issueTaxInvoice!==false;
      var apply=function(patch){setAddItemForm(Object.assign({},addItemForm,patch))};
      return (<SettlementItemDrawer mode="add" isMobile={isMobile} title="수기 항목 추가" subtitle={(scopeTitle?scopeTitle+' · ':'')+monthLabel()}
        form={addItemForm} onChange={apply} onSubmit={submitAddItem} onClose={function(){setShowAddItem(false)}}
        issue={issue} preview={settlementPreviewOf(addItemForm,issue)} legacyReissueSum={0}
        districtListId="stl-districts" workTypeField={workTypeField(addItemForm,apply,is)}
        noteCombo={<PresetCombo presets={NOTE_PRESETS} is={is} placeholder="비고 직접 입력" value={addItemForm.note} onChange={function(v){apply({note:v})}}/>}
        extraCombo={<ExtraServiceCombo catalog={extraCatalog} value={addItemForm.extraServiceNote} onChange={function(v){apply({extraServiceNote:v})}}/>}/>);
    })()}

    {/* 수기 미수 조정 등록 — 단위(u:) 스코프에서만 열린다 (AdjustButton). 귀속월 = 보고 있는 달 */}
    {showAdjust&&!!stlUnitId&&<SettlementAdjustModal agencyId={stlAgencyId} unitId={stlUnitId} unitName={curUnit?curUnit.name:''} month={stlMonth}
      showToast={showToast} onClose={function(){setShowAdjust(false)}} onSaved={function(){setShowAdjust(false);reloadStl()}}/>}

    {/* 거래처 픽커 — 대상 칩이 여는 우측 드로어. 대상 축만 바꾸고(화면은 유지) 선택 즉시 닫힌다.
        목록 맨 위 '👥 전체 거래처' 행이 전체로 돌아가는 경로 (ClientScopePanel). */}
    {!!pickerOpen&&<div onClick={function(){setPickerOpen(false)}}
      style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',zIndex:1500}}>
      <div onClick={function(e){e.stopPropagation()}}
        style={{position:'absolute',top:0,left:0,bottom:0,width:isMobile?'100vw':560,maxWidth:isMobile?'100vw':'92vw',background:'#fff',boxShadow:'16px 0 48px rgba(0,0,0,0.25)',display:'flex',flexDirection:'column'}}>
        <button title="닫기" onClick={function(){setPickerOpen(false)}}
          style={isMobile
            ?{position:'absolute',top:6,right:6,zIndex:2,background:'none',border:'none',fontSize:20,color:'#94a3b8',cursor:'pointer',lineHeight:1,padding:0,width:40,height:40}
            :{position:'absolute',top:10,right:12,zIndex:2,background:'none',border:'none',fontSize:17,color:'#94a3b8',cursor:'pointer',lineHeight:1,padding:2}}>✕</button>
        <ClientScopePanel fluid drawer autoFocus allRow data={scopeData} value={stlScope}
          onChange={function(v){setPickerOpen(false);pickScope(v)}} loading={!scopeReady}/>
      </div>
    </div>}

    {/* 말소증 뷰어 + [업체에 공유] — 장부 표 [말소증] 버튼 (공유는 정산 항목 전용 POST) */}
    {!!viewCertItem&&<MalsoCertViewer
      title={'말소증'+(viewCertItem.carNo?' — '+viewCertItem.carNo:'')}
      carNo={viewCertItem.carNo}
      fetchDoc={function(){return authFetch(API+'/settlement-items/'+viewCertItem.id+'/malso-doc')}}
      share={function(){return authFetch(API+'/settlement-items/'+viewCertItem.id+'/malso-share',{method:'POST'}).then(function(r){return r.json()})}}
      showToast={showToast}
      onClose={function(){setViewCertItem(null)}}/>}
  </div>);
}

export default CancelAgencySettlement;
